/**
 * CanvasView · P2.6.c
 *
 * Minimal canvas rendering · derives node state from audit_log events:
 *   - canvas.create_node    → add node row
 *   - canvas.update_node_status → status change (explicit MCP tool)
 *   - task_run.* lifecycle   → status change (P3 P1-6 · auto from scheduler)
 *
 * P3 P1-6 maps backend task_run.* audits to canvas node 8-state:
 *   dispatched → running
 *   waiting_worker / waiting_tree → waiting
 *   finished → done · error → blocked
 *   canceled / skipped → excluded
 *
 * v0.1 scope (minimum viable):
 *   - Flat list of nodes (no graph layout · no dragging · no edges)
 *   - 8-state badge per node (dormant/running/thinking/waiting/blocked/
 *     done/excluded/dirty · per CLAUDE.md rule 4)
 *   - Click → console.log node_id (real interactivity in P3.4 canvas full)
 *
 * Out of scope (deferred to P3.4 canvas v0.2):
 *   - SVG layout · zoom/pan · drag · port connections
 *   - Edges (canvas.create_edge events not implemented backend-side)
 *   - Node detail panel
 *   - Decision branching visualization (4-state)
 *
 * The 8-state palette uses design-system tokens · per CLAUDE.md rule 2
 * no hardcoded colors.
 */

import { useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  type Node as RFNode,
  type Edge as RFEdge,
  type NodeProps,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { useAuditStore, selectSessionEvents } from '../stores/audit-store';
import type { WsEvent } from '../lib/ws-client';
import { Icon } from './Icon';

/** 8-state node enum (CLAUDE.md rule 4 / domain-model §2.6) */
export type NodeStatus =
  | 'dormant'
  | 'running'
  | 'thinking'
  | 'waiting'
  | 'blocked'
  | 'done'
  | 'excluded'
  | 'dirty';

const VALID_STATUSES: ReadonlySet<NodeStatus> = new Set([
  'dormant', 'running', 'thinking', 'waiting',
  'blocked', 'done', 'excluded', 'dirty',
]);

export type NodeKind = 'task' | 'decision' | 'human' | 'output';
const VALID_KINDS: ReadonlySet<NodeKind> = new Set(['task', 'decision', 'human', 'output']);

export type CanvasBranchState = 'active' | 'parallel' | 'dormant' | 'excluded';

export interface CanvasBranch {
  label: string;
  target_node_id: string;
  state: CanvasBranchState;
}

export interface CanvasMergeHandoff {
  source_node_ids: string[];
  artifact_refs: unknown[];
  handoff_summary: string | null;
}

export interface CanvasNode {
  node_id: string;
  node_kind: NodeKind;
  status: NodeStatus;
  /** Human-readable task title. P4.1 prefers payload.title / nodes.title,
   *  and falls back to legacy payload.meta.title. */
  title: string | null;
  /** Short task description. P4.1 prefers payload.description, with
   *  payload.meta.description as legacy fallback. */
  description: string | null;
  /** Suggested execution agent for planner-owned nodes. */
  assigned_agent_id: string | null;
  planner_key: string | null;
  source_audit_start: number | null;
  source_audit_end: number | null;
  plan_revision: number | null;
  superseded_at: number | null;
  acceptance_criteria: string[];
  context_refs: unknown[];
  boundaries: unknown;
  branches?: CanvasBranch[];
  merge?: CanvasMergeHandoff | null;
  /** Audit_id of the create event · used as creation order. */
  created_audit_id: number;
  /** Audit_id of the latest status update (or create) · used for tie-break. */
  last_audit_id: number;
  /** v0.1 user-feedback 2026-05-12 · canvas-as-task-tree · backend assigns
   *  positions when parent_node_id is supplied (child auto-positioned beside
   *  parent · siblings stacked vertically). Defaults 0,0 when agent gave no
   *  hint AND no parent · these nodes overlap visually · the renderer
   *  applies a fallback grid for zero-position nodes. */
  position_x: number;
  position_y: number;
}

/** v0.1 user-feedback 2026-05-12 · directed edge between two canvas nodes. */
export interface CanvasEdge {
  edge_id: string;
  from_node_id: string;
  to_node_id: string;
  edge_kind: 'flow' | 'data' | 'dependency';
  created_audit_id: number;
}

const VALID_EDGE_KINDS: ReadonlySet<string> = new Set(['flow', 'data', 'dependency']);

export type CanvasImpactSourceKind = 'node' | 'node_output' | 'artifact' | 'knowledge';
export type CanvasImpactKind = 'direct' | 'indirect';
export type CanvasImpactStatus = 'stale' | 'dirty' | 'acknowledged' | 'rerun_queued' | 'resolved';
export type CanvasImpactAction = 'rerun_all' | 'rerun_selected' | 'keep_old' | 'branch';

const VALID_IMPACT_SOURCE_KINDS: ReadonlySet<string> = new Set(['node', 'node_output', 'artifact', 'knowledge']);
const VALID_IMPACT_KINDS: ReadonlySet<string> = new Set(['direct', 'indirect']);
const VALID_IMPACT_STATUSES: ReadonlySet<string> = new Set([
  'stale',
  'dirty',
  'acknowledged',
  'rerun_queued',
  'resolved',
]);

export interface CanvasImpactMark {
  source_kind: CanvasImpactSourceKind;
  source_id: string;
  source_node_id: string | null;
  source_run_id: string | null;
  affected_node_id: string;
  impact_kind: CanvasImpactKind;
  impact_status: CanvasImpactStatus;
  depth: number;
  cause_chain: Array<Record<string, unknown>>;
  created_at: number | null;
  updated_at: number | null;
  last_audit_id: number;
}

export interface CanvasImpactNodeSummary {
  active_count: number;
  direct_count: number;
  indirect_count: number;
  dirty_count: number;
  stale_count: number;
  rerun_queued_count: number;
  highest_status: CanvasImpactStatus;
}

export interface CanvasImpactActionResult {
  ok: boolean;
  audit_id?: number | null;
  action?: CanvasImpactAction;
  updated_mark_status?: CanvasImpactStatus;
  selected_node_ids?: string[];
  rerun_node_ids?: string[];
  skipped_nodes?: Array<{ node_id: string; reason: string }>;
  error?: string;
  error_code?: string;
}

function textField(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function intField(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function stringArrayField(value: unknown): string[] | null {
  const parsed = typeof value === 'string' ? parseJsonValue(value) : value;
  if (!Array.isArray(parsed)) return null;
  const out = parsed
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim());
  return out.length > 0 ? out : [];
}

function arrayField(value: unknown): unknown[] | null {
  const parsed = typeof value === 'string' ? parseJsonValue(value) : value;
  return Array.isArray(parsed) ? parsed : null;
}

function recordArrayField(value: unknown): Array<Record<string, unknown>> {
  const parsed = arrayField(value) ?? [];
  return parsed
    .map((item) => objectField(item))
    .filter((item) => Object.keys(item).length > 0);
}

function branchesField(value: unknown): CanvasBranch[] | null {
  const parsed = typeof value === 'string' ? parseJsonValue(value) : value;
  if (!Array.isArray(parsed)) return null;
  const branches = parsed.flatMap((item): CanvasBranch[] => {
    const record = objectField(item);
    const label = textField(record.label);
    const target = textField(record.target_node_id);
    const state = textField(record.state);
    if (!label || !target) return [];
    if (state !== 'active' && state !== 'parallel' && state !== 'dormant' && state !== 'excluded') return [];
    return [{ label, target_node_id: target, state }];
  });
  return branches.length > 0 ? branches : null;
}

function mergeField(value: unknown): CanvasMergeHandoff | null {
  const record = objectField(value);
  if (Object.keys(record).length === 0) return null;
  const source_node_ids = stringArrayField(record.source_node_ids) ?? [];
  const artifact_refs = arrayField(record.artifact_refs) ?? [];
  const handoff_summary = textField(record.handoff_summary);
  if (source_node_ids.length === 0 && artifact_refs.length === 0 && !handoff_summary) return null;
  return { source_node_ids, artifact_refs, handoff_summary };
}

function parseJsonValue(value: string): unknown {
  if (value.trim().length === 0) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function objectField(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== 'string' || value.trim().length === 0) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {};
  } catch {
    return {};
  }
}

function eventOrder(evt: WsEvent): number | null {
  if (evt.kind === 'workflow.snapshot') {
    const snapshotAuditId = intField(objectField(evt.payload).snapshot_audit_id);
    if (snapshotAuditId !== null && snapshotAuditId >= 0) return snapshotAuditId + 0.5;
    return null;
  }
  if (typeof evt.audit_id !== 'number' || !Number.isSafeInteger(evt.audit_id) || evt.audit_id <= 0) {
    return null;
  }
  return evt.audit_id;
}

function eventAuditMarker(evt: WsEvent, orderId: number): number {
  if (evt.kind === 'workflow.snapshot') {
    return intField(objectField(evt.payload).snapshot_audit_id) ?? Math.floor(orderId);
  }
  return evt.audit_id ?? Math.floor(orderId);
}

/**
 * Artifact derived from an `artifact.save` audit event. P2.8.D1 surfaces
 * these as cards in CanvasView so users can see + download what the agent
 * produced.
 */
export interface CanvasArtifact {
  path: string;
  hash: string;
  size: number;
  content_type: string | null;
  audit_id: number;
  ts: number;
  /** Optional · ties this artifact to the producing node (if backend set node_id). */
  node_id: string | null;
  /** v0.1 round 5 · the task_run that was active when artifact.save fired.
   *  Same run_id covers all artifacts produced during one chat dispatch.
   *  Used by NodeDetailPanel to scope an output node's deliverables to
   *  the conversation that built it (instead of leaking historical
   *  artifacts from earlier task_runs in the same session). */
  run_id: string | null;
}

export type ArtifactTypeFilter = 'all' | 'pdf' | 'text' | 'image' | 'other';
export type ArtifactTimeFilter = 'all' | 'recent';

const ARTIFACT_FILTER_LABEL: Record<ArtifactTypeFilter, string> = {
  all: '全部',
  pdf: 'PDF',
  text: '文本',
  image: '图片',
  other: '其他',
};

const ARTIFACT_TIME_FILTER_LABEL: Record<ArtifactTimeFilter, string> = {
  all: '全部时间',
  recent: '最近',
};

const ARTIFACT_RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;

export function artifactFilename(artifact: CanvasArtifact): string {
  return artifact.path.split('/').filter(Boolean).pop() ?? artifact.path;
}

export function artifactType(artifact: CanvasArtifact): Exclude<ArtifactTypeFilter, 'all'> {
  const contentType = artifact.content_type?.toLowerCase() ?? '';
  const path = artifact.path.toLowerCase();
  if (contentType.includes('pdf') || path.endsWith('.pdf')) return 'pdf';
  if (contentType.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(path)) return 'image';
  if (
    contentType.startsWith('text/') ||
    contentType.includes('json') ||
    /\.(md|txt|csv|json|jsonl|ts|tsx|js|jsx|py|html|css|sql|xml|yaml|yml)$/.test(path)
  ) {
    return 'text';
  }
  return 'other';
}

export function artifactTypeLabel(artifact: CanvasArtifact): string {
  return ARTIFACT_FILTER_LABEL[artifactType(artifact)];
}

export function artifactsForPanel(
  artifacts: CanvasArtifact[],
  filter: ArtifactTypeFilter,
  timeFilter: ArtifactTimeFilter = 'all',
): CanvasArtifact[] {
  let visible = filter === 'all'
    ? artifacts
    : artifacts.filter((artifact) => artifactType(artifact) === filter);
  if (timeFilter === 'recent') {
    const newestTs = visible.reduce((max, artifact) => Math.max(max, artifact.ts), -Infinity);
    const cutoff = Number.isFinite(newestTs) ? newestTs - ARTIFACT_RECENT_WINDOW_MS : Infinity;
    visible = visible.filter((artifact) => artifact.ts >= cutoff);
  }
  return [...visible].sort((a, b) => b.audit_id - a.audit_id);
}

export interface CanvasArtifactVersion {
  version: number;
  path: string;
  hash: string;
  size: number;
  content_type: string | null;
  audit_id: number;
  ts: number;
  node_id: string | null;
  run_id: string | null;
  current: boolean;
}

export interface CanvasArtifactProducedBy {
  node_id: string | null;
  run_id: string | null;
  output_id: string | null;
  agent_id: string | null;
  audit_id: number | null;
  version: number | null;
}

export interface CanvasArtifactReferencedBy {
  node_id: string;
  title: string | null;
  source: 'context_ref' | 'merge_ref';
  ref_id: string | null;
  output_id: string | null;
  run_id: string | null;
}

export interface CanvasArtifactLineage {
  versions: CanvasArtifactVersion[];
  producedBy: CanvasArtifactProducedBy[];
  referencedBy: CanvasArtifactReferencedBy[];
}

function artifactRefCandidates(ref: unknown): string[] {
  const out = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (trimmed.length > 0) out.add(trimmed);
  };
  if (typeof ref === 'string') {
    add(ref);
    const parsed = parseJsonValue(ref);
    if (parsed && parsed !== ref) {
      for (const value of artifactRefCandidates(parsed)) out.add(value);
    }
    return [...out];
  }
  const record = objectField(ref);
  for (const key of ['ref_id', 'artifact_id', 'hash', 'path', 'uri', 'url', 'href', 'output_id', 'run_id', 'node_id']) {
    add(record[key]);
  }
  for (const nestedKey of ['locator', 'artifact', 'blob', 'target']) {
    const nested = objectField(record[nestedKey]);
    for (const key of ['ref_id', 'artifact_id', 'hash', 'path', 'uri', 'url', 'href', 'output_id', 'run_id', 'node_id']) {
      add(nested[key]);
    }
  }
  return [...out];
}

function artifactRefMatchesIdentity(ref: unknown, artifact: { path: string; hash: string }): boolean {
  const wanted = new Set([
    artifact.path,
    artifact.hash,
    `artifact:${artifact.path}`,
    `artifact:${artifact.hash}`,
    `file:${artifact.path}`,
    `sha256:${artifact.hash}`,
  ]);
  for (const candidate of artifactRefCandidates(ref)) {
    if (wanted.has(candidate)) return true;
    if (candidate.endsWith(`/${artifact.path}`) || candidate.endsWith(`:${artifact.path}`)) return true;
  }
  return false;
}

function collectContextRefItems(value: unknown): unknown[] {
  if (typeof value === 'string') {
    const parsed = parseJsonValue(value);
    return parsed === null ? [value] : collectContextRefItems(parsed);
  }
  if (Array.isArray(value)) return value.flatMap((item) => collectContextRefItems(item));
  const record = objectField(value);
  if (Object.keys(record).length === 0) return [];
  const nested: unknown[] = [];
  for (const key of ['required', 'optional', 'context_refs', 'refs', 'artifact_refs', 'artifacts']) {
    const items = arrayField(record[key]);
    if (items) nested.push(...items.flatMap((item) => collectContextRefItems(item)));
  }
  return nested.length > 0 ? [record, ...nested] : [record];
}

function refDisplayId(ref: unknown): string | null {
  if (typeof ref === 'string') return ref.trim().slice(0, 80) || null;
  const record = objectField(ref);
  return textField(record.title)
    ?? textField(record.ref_id)
    ?? textField(record.output_id)
    ?? textField(record.path)
    ?? textField(record.uri)
    ?? textField(objectField(record.locator).path)
    ?? textField(objectField(record.locator).url);
}

function artifactVersionsForPath(
  artifact: CanvasArtifact,
  events: WsEvent[],
  workspaceId?: string,
): CanvasArtifactVersion[] {
  const versions: CanvasArtifact[] = [];
  for (const evt of events) {
    if (evt.kind !== 'artifact.save') continue;
    if (typeof evt.audit_id !== 'number' || !Number.isSafeInteger(evt.audit_id) || evt.audit_id <= 0) continue;
    if (typeof evt.ts !== 'number' || !Number.isFinite(evt.ts)) continue;
    if (workspaceId && evt.workspace_id !== workspaceId) continue;
    const payload = evt.payload ?? {};
    const path = typeof payload.path === 'string' ? payload.path : null;
    const hash = typeof payload.hash === 'string' ? payload.hash : null;
    const size = typeof payload.size === 'number' && Number.isFinite(payload.size) ? payload.size : null;
    if (path !== artifact.path || !hash || size === null) continue;
    if (!/^[a-f0-9]{64}$/.test(hash)) continue;
    versions.push({
      path,
      hash,
      size,
      content_type: typeof payload.content_type === 'string' ? payload.content_type : null,
      audit_id: evt.audit_id,
      ts: evt.ts,
      node_id: typeof payload.node_id === 'string' ? payload.node_id : null,
      run_id: typeof payload.run_id === 'string' ? payload.run_id : null,
    });
  }
  const normalized = versions.length > 0 ? versions : [artifact];
  return normalized
    .sort((a, b) => a.audit_id - b.audit_id)
    .map((version, index) => ({
      ...version,
      version: index + 1,
      current: version.audit_id === artifact.audit_id && version.hash === artifact.hash,
    }))
    .sort((a, b) => b.audit_id - a.audit_id);
}

function versionForProducer(
  versions: CanvasArtifactVersion[],
  input: { audit_id?: number | null; run_id?: string | null; hash?: string | null },
): number | null {
  if (typeof input.audit_id === 'number') {
    const byAudit = versions.find((version) => version.audit_id === input.audit_id);
    if (byAudit) return byAudit.version;
  }
  if (input.run_id) {
    const byRun = versions.find((version) => version.run_id === input.run_id);
    if (byRun) return byRun.version;
  }
  if (input.hash) {
    const byHash = versions.find((version) => version.hash === input.hash);
    if (byHash) return byHash.version;
  }
  return null;
}

/**
 * Build artifact lineage for the panel. This is intentionally derived from
 * existing audit/snapshot data: `artifact.save` gives versions, node_outputs
 * gives the run/output wrapper, and node context_refs gives reverse usage.
 */
export function buildArtifactLineage(
  artifact: CanvasArtifact,
  events: WsEvent[],
  nodes: CanvasNode[],
  workspaceId?: string,
): CanvasArtifactLineage {
  const versions = artifactVersionsForPath(artifact, events, workspaceId);
  const producedBy: CanvasArtifactProducedBy[] = [];
  const referencedBy: CanvasArtifactReferencedBy[] = [];

  const upsertProduced = (entry: CanvasArtifactProducedBy) => {
    const existingIndex = producedBy.findIndex((prior) => {
      if (entry.output_id && prior.output_id === entry.output_id) return true;
      if (entry.run_id && entry.node_id && prior.run_id === entry.run_id && prior.node_id === entry.node_id) return true;
      if (entry.audit_id && prior.audit_id === entry.audit_id) return true;
      return false;
    });
    if (existingIndex >= 0) {
      const prior = producedBy[existingIndex];
      producedBy[existingIndex] = {
        node_id: entry.node_id ?? prior.node_id,
        run_id: entry.run_id ?? prior.run_id,
        output_id: entry.output_id ?? prior.output_id,
        agent_id: entry.agent_id ?? prior.agent_id,
        audit_id: entry.audit_id ?? prior.audit_id,
        version: entry.version ?? prior.version,
      };
      return;
    }
    producedBy.push(entry);
  };

  for (const version of versions) {
    if (!version.node_id && !version.run_id) continue;
    upsertProduced({
      node_id: version.node_id,
      run_id: version.run_id,
      output_id: null,
      agent_id: null,
      audit_id: version.audit_id,
      version: version.version,
    });
  }

  for (const evt of events) {
    if (evt.kind !== 'workflow.snapshot') continue;
    if (workspaceId && evt.workspace_id !== workspaceId) continue;
    const payload = objectField(evt.payload);
    const snapshotAuditId = intField(payload.snapshot_audit_id);
    const outputs = arrayField(payload.node_outputs) ?? [];
    for (const rawOutput of outputs) {
      const output = objectField(rawOutput);
      const outputRefs = [
        ...collectContextRefItems(output.artifacts),
        ...collectContextRefItems(output.artifact_refs),
      ];
      const runId = textField(output.run_id);
      const outputMatches = outputRefs.some((ref) => artifactRefMatchesIdentity(ref, artifact))
        || Boolean(runId && versions.some((version) => version.run_id === runId));
      if (!outputMatches) continue;
      const outputId = textField(output.output_id);
      const nodeId = textField(output.node_id);
      const version = versionForProducer(versions, {
        run_id: runId,
        audit_id: intField(output.source_audit_end),
        hash: outputRefs
          .map((ref) => artifactRefCandidates(ref).find((candidate) => /^[a-f0-9]{64}$/.test(candidate)))
          .find(Boolean) ?? null,
      });
      upsertProduced({
        node_id: nodeId,
        run_id: runId,
        output_id: outputId,
        agent_id: textField(output.agent_id),
        audit_id: intField(output.source_audit_end) ?? snapshotAuditId,
        version,
      });
    }
  }

  const producedOutputIds = new Set(producedBy.map((entry) => entry.output_id).filter((v): v is string => Boolean(v)));
  const producedRunIds = new Set(producedBy.map((entry) => entry.run_id).filter((v): v is string => Boolean(v)));
  const producedNodeIds = new Set(producedBy.map((entry) => entry.node_id).filter((v): v is string => Boolean(v)));
  const addReferenced = (node: CanvasNode, ref: unknown, source: CanvasArtifactReferencedBy['source']) => {
    const candidates = artifactRefCandidates(ref);
    const matchesArtifact = artifactRefMatchesIdentity(ref, artifact);
    const matchesProducedOutput = candidates.some((candidate) => producedOutputIds.has(candidate));
    const matchesProducedRun = candidates.some((candidate) => producedRunIds.has(candidate));
    const refKind = textField(objectField(ref).kind) ?? textField(objectField(ref).type);
    const matchesProducedNodeOutput = refKind === 'node_output'
      && candidates.some((candidate) => producedNodeIds.has(candidate));
    if (!matchesArtifact && !matchesProducedOutput && !matchesProducedRun && !matchesProducedNodeOutput) return;
    const key = `${node.node_id}:${source}:${refDisplayId(ref) ?? candidates.join('|')}`;
    if (referencedBy.some((entry) => `${entry.node_id}:${entry.source}:${entry.ref_id ?? entry.output_id ?? entry.run_id ?? ''}` === key)) return;
    referencedBy.push({
      node_id: node.node_id,
      title: node.title,
      source,
      ref_id: refDisplayId(ref),
      output_id: candidates.find((candidate) => producedOutputIds.has(candidate)) ?? textField(objectField(ref).output_id),
      run_id: candidates.find((candidate) => producedRunIds.has(candidate)) ?? textField(objectField(ref).run_id),
    });
  };

  for (const node of nodes) {
    for (const ref of collectContextRefItems(node.context_refs)) {
      addReferenced(node, ref, 'context_ref');
    }
    for (const ref of collectContextRefItems(node.merge?.artifact_refs ?? [])) {
      addReferenced(node, ref, 'merge_ref');
    }
  }

  return {
    versions,
    producedBy: producedBy.sort((a, b) => (b.version ?? 0) - (a.version ?? 0)),
    referencedBy: referencedBy.sort((a, b) => a.node_id.localeCompare(b.node_id)),
  };
}

/**
 * Extract artifacts from `artifact.save` events. Pure function · exported
 * for unit testing.
 *
 * Dedup model · codex P2.8.D1 R1 P1 fix:
 *   - Group by `path` (NOT by hash) · keep the entry with the highest
 *     `audit_id` for that path. This means an agent that saves the same
 *     file twice (with edits between) shows one card with the latest hash ·
 *     not two cards both labelled `report.md`.
 *   - Two different paths with the same hash (rare · agent saves identical
 *     content under two names) show two cards · because they are two
 *     workspace files even if blob storage dedups. Prior version kept first
 *     by hash · which hid `final.md` after `draft.md` of identical content.
 *   - Returned list sorted by audit_id ASC so creation order is stable.
 *
 * Filtering: drops events with non-positive / non-safe-integer audit_id ·
 * non-finite ts · malformed payload (missing path / hash / size · bad hash
 * format). Optional `workspaceId` arg adds belt-and-suspenders cross-tenant
 * filter (mirrors `eventsToNodes` · A18 paranoid defense).
 */
export function eventsToArtifacts(events: WsEvent[], workspaceId?: string): CanvasArtifact[] {
  // path → latest CanvasArtifact (highest audit_id)
  const byPath = new Map<string, CanvasArtifact>();
  // We don't need to pre-sort · we keep the entry with max audit_id per path.
  for (const evt of events) {
    if (evt.kind !== 'artifact.save') continue;
    if (typeof evt.audit_id !== 'number') continue;
    if (!Number.isSafeInteger(evt.audit_id) || evt.audit_id <= 0) continue;
    if (typeof evt.ts !== 'number' || !Number.isFinite(evt.ts)) continue;
    if (workspaceId && evt.workspace_id !== workspaceId) continue;
    const payload = evt.payload ?? {};
    const path = typeof payload.path === 'string' ? payload.path : null;
    const hash = typeof payload.hash === 'string' ? payload.hash : null;
    const size = typeof payload.size === 'number' && Number.isFinite(payload.size) ? payload.size : null;
    if (!path || !hash || size === null) continue;
    if (!/^[a-f0-9]{64}$/.test(hash)) continue;
    const candidate: CanvasArtifact = {
      path,
      hash,
      size,
      content_type: typeof payload.content_type === 'string' ? payload.content_type : null,
      audit_id: evt.audit_id,
      ts: evt.ts,
      node_id: typeof payload.node_id === 'string' ? payload.node_id : null,
      run_id: typeof payload.run_id === 'string' ? payload.run_id : null,
    };
    const prior = byPath.get(path);
    if (!prior || evt.audit_id > prior.audit_id) {
      byPath.set(path, candidate);
    }
  }
  return [...byPath.values()].sort((a, b) => a.audit_id - b.audit_id);
}

/**
 * Run summary derived from task_run.* events. P3 P0-4 · powers the
 * NodeDetailPanel run history list. Pure · exported for unit testing.
 *
 * One CanvasNodeRun per task_run.created · subsequent task_run.* events
 * for the same run_id update its status / endedAt.
 */
export interface CanvasNodeRun {
  run_id: string;
  agent_id: string | null;
  /** Backend run lifecycle states (separate from canvas 8-state). */
  status: 'queued' | 'running' | 'waiting' | 'success' | 'failed' | 'canceled';
  /** audit_id of task_run.created · acts as creation order. */
  created_audit_id: number;
  /** ts of task_run.created (creation time). */
  created_ts: number;
  /** ts of terminal event (finished/error/canceled/skipped) · null if still in flight. */
  ended_ts: number | null;
  /** P3.14 · backend's `error_detail` string for failed runs (raw "code: message"
   *  · NodeDetailPanel feeds to translateError for Chinese display). Undefined
   *  for non-failed runs OR failed runs whose audit didn't carry the field. */
  error_detail?: string;
  /** Optional P3.3/P5 monitor fields · populated from cost.reported or terminal payloads. */
  cost_tokens_in?: number;
  cost_tokens_out?: number;
  cost_usd_micros?: number;
}

/**
 * Extract task_run history for a given node_id. P3 P0-4. Pure function
 * · exported for unit testing.
 *
 *   - Reads task_run.* events filtered to one node (via run_id→node_id
 *     map built from task_run.created events).
 *   - Returns latest-status-per-run, sorted by created_audit_id ASC.
 *   - Drops runs whose task_run.created we never saw (orphan terminals).
 */
export function eventsToRunsForNode(
  events: WsEvent[],
  nodeId: string,
  workspaceId?: string,
): CanvasNodeRun[] {
  const runs = new Map<string, CanvasNodeRun & { last_audit_id: number }>();
  const sorted = [...events].sort((a, b) => (a.audit_id ?? 0) - (b.audit_id ?? 0));

  for (const evt of sorted) {
    if (typeof evt.audit_id !== 'number') continue;
    if (!Number.isSafeInteger(evt.audit_id) || evt.audit_id <= 0) continue;
    if (workspaceId && evt.workspace_id !== workspaceId) continue;

    // P3-deploy 2026-05-11 · cleanup events drop the run from this node's
    // history (mirrors the canvas-level deletion in eventsToNodes).
    if (evt.kind === 'node.cleanup') {
      const cleanupPayload = evt.payload ?? {};
      if (cleanupPayload.node_id === nodeId) {
        const cleanupRun = typeof cleanupPayload.run_id === 'string' ? cleanupPayload.run_id : null;
        if (cleanupRun) runs.delete(cleanupRun);
      }
      continue;
    }

    if (typeof evt.kind !== 'string' || (!evt.kind.startsWith('task_run.') && evt.kind !== 'cost.reported')) continue;
    const payload = evt.payload ?? {};
    const run_id = typeof payload.run_id === 'string' ? payload.run_id : null;
    if (!run_id) continue;

    if (evt.kind === 'task_run.created') {
      const node_id = typeof payload.node_id === 'string' ? payload.node_id : null;
      if (node_id !== nodeId) continue;
      if (runs.has(run_id)) continue; // first-create wins
      runs.set(run_id, {
        run_id,
        agent_id: typeof payload.agent_id === 'string' ? payload.agent_id : null,
        status: 'queued',
        created_audit_id: evt.audit_id,
        created_ts: typeof evt.ts === 'number' && Number.isFinite(evt.ts) ? evt.ts : 0,
        ended_ts: null,
        last_audit_id: evt.audit_id,
      });
      continue;
    }

    const existing = runs.get(run_id);
    if (!existing) continue; // orphan · we never saw the task_run.created for this node
    if (evt.audit_id <= existing.last_audit_id) continue;

    if (evt.kind === 'cost.reported') {
      runs.set(run_id, {
        ...existing,
        cost_tokens_in: typeof payload.tokens_in === 'number' && Number.isFinite(payload.tokens_in) ? payload.tokens_in : existing.cost_tokens_in,
        cost_tokens_out: typeof payload.tokens_out === 'number' && Number.isFinite(payload.tokens_out) ? payload.tokens_out : existing.cost_tokens_out,
        cost_usd_micros: typeof payload.cost_usd_micros === 'number' && Number.isFinite(payload.cost_usd_micros) ? payload.cost_usd_micros : existing.cost_usd_micros,
        last_audit_id: evt.audit_id,
      });
      continue;
    }

    const ts = typeof evt.ts === 'number' && Number.isFinite(evt.ts) ? evt.ts : null;
    const dispatchedFalse = payload.dispatched === false;
    let nextStatus: CanvasNodeRun['status'] = existing.status;
    let nextEndedTs: number | null = existing.ended_ts;
    let nextErrorDetail: string | undefined = existing.error_detail;

    if (evt.kind === 'task_run.dispatched') {
      nextStatus = 'running';
    } else if (evt.kind === 'task_run.queued_busy' ||
               evt.kind === 'task_run.waiting_worker' ||
               evt.kind === 'task_run.waiting_tree') {
      nextStatus = 'waiting';
    } else if (evt.kind === 'task_run.finished') {
      nextStatus = 'success';
      nextEndedTs = ts ?? nextEndedTs;
    } else if (evt.kind === 'task_run.error') {
      nextStatus = 'failed';
      nextEndedTs = ts ?? nextEndedTs;
      // P3.14 · capture error_detail string for NodeDetailPanel to translate
      const ed = typeof payload.error_detail === 'string' ? payload.error_detail : undefined;
      if (ed) nextErrorDetail = ed;
    } else if (evt.kind === 'task_run.canceled' ||
               evt.kind === 'task_run.skipped' ||
               ((evt.kind === 'task_run.cancel_requested' ||
                 evt.kind === 'task_run.interrupt_requested') && dispatchedFalse)) {
      nextStatus = 'canceled';
      nextEndedTs = ts ?? nextEndedTs;
    } else {
      // *_requested with dispatched===true · in-flight · don't change run state.
      continue;
    }

    runs.set(run_id, {
      ...existing,
      status: nextStatus,
      ended_ts: nextEndedTs,
      error_detail: nextErrorDetail,
      cost_tokens_in: typeof payload.cost_tokens_in === 'number' && Number.isFinite(payload.cost_tokens_in) ? payload.cost_tokens_in : existing.cost_tokens_in,
      cost_tokens_out: typeof payload.cost_tokens_out === 'number' && Number.isFinite(payload.cost_tokens_out) ? payload.cost_tokens_out : existing.cost_tokens_out,
      cost_usd_micros: typeof payload.cost_usd_micros === 'number' && Number.isFinite(payload.cost_usd_micros) ? payload.cost_usd_micros : existing.cost_usd_micros,
      last_audit_id: evt.audit_id,
    });
  }

  return [...runs.values()]
    .map(({ last_audit_id, ...rest }) => rest)
    .sort((a, b) => a.created_audit_id - b.created_audit_id);
}

/**
 * Reduce audit events into the current node-state map. Pure function ·
 * exported for unit testing. Drops malformed events silently (defense in
 * depth · ws-client / chat-panel already validate · we re-validate here).
 *
 * Optional `workspaceId` arg adds belt-and-suspenders cross-tenant filtering.
 * The selectors / store binding already isolate · this is paranoid defense.
 * (codex-review 2026-04-29 P2.6.c R1 P2)
 */
export function eventsToNodes(events: WsEvent[], workspaceId?: string): CanvasNode[] {
  const nodes = new Map<string, CanvasNode>();
  const runIdToNodeId = new Map<string, string>();

  // Sort by reducer event order, not raw audit_id. Synthetic workflow.snapshot
  // events carry no audit_id but represent "state after snapshot_audit_id";
  // ordering them at 0 would let a snapshot erase live patch metadata before
  // the matching workflow.patch_applied event is reduced.
  const sorted = [...events].sort((a, b) => (eventOrder(a) ?? Number.POSITIVE_INFINITY) - (eventOrder(b) ?? Number.POSITIVE_INFINITY));

  // P3-deploy 2026-05-11 codex P1 #5 · legacy-audit gap-fill recovery.
  //
  // Modern (post-2026-05-11) flow · /finish emits either node.cleanup OR
  // a canvas.create_node + canvas.update_node_status backfill pair. The
  // reducer's task_run.created handler intentionally does NOT auto-create
  // a canvas node · because for in-flight runs the SPA waits for the
  // resolution audit and for completed runs the backfill creates the node.
  //
  // But pre-2026-05-11 runs (already-finished runs sitting in audit_log)
  // never received cleanup or backfill audits · they had only task_run.*
  // events + an implicit assumption that the SPA reducer auto-creates
  // from task_run.created. If we just stopped auto-creating · those
  // historical nodes would vanish on every page refresh and gap-fill.
  //
  // Pre-pass · build the set of runs that have BEEN explicitly resolved
  // by modern audits (cleanup OR a backfill canvas.create_node tagged
  // with backfilled_for_run_id). For task_run.created from a run NOT in
  // this set AND that has a terminal event in the stream · we treat it
  // as legacy and auto-create the node. Live in-flight runs (no terminal
  // event yet) stay hidden until their resolution audit arrives via WS.
  const resolvedRuns = new Set<string>();
  const finishedRuns = new Set<string>();
  // Codex round 3 P1 · modern terminal audits carry resolution_emitted:true ·
  // SPA can identify them as modern even when the matching cleanup/backfill
  // audit hasn't been received yet (live WS interleave or lost broadcast).
  // Legacy auto-create skipped if the run's terminal payload has this marker.
  const modernTerminalRuns = new Set<string>();
  for (const evt of sorted) {
    if (eventOrder(evt) === null) continue;
    const payload = evt.payload ?? {};
    if (evt.kind === 'node.cleanup') {
      const r = typeof payload.run_id === 'string' ? payload.run_id : null;
      if (r) resolvedRuns.add(r);
    } else if (evt.kind === 'canvas.create_node') {
      const meta = (payload as any).meta ?? {};
      const r = typeof meta.backfilled_for_run_id === 'string' ? meta.backfilled_for_run_id : null;
      if (r) resolvedRuns.add(r);
    } else if (
      evt.kind === 'task_run.finished' ||
      evt.kind === 'task_run.error' ||
      evt.kind === 'task_run.canceled' ||
      evt.kind === 'task_run.skipped'
    ) {
      const r = typeof payload.run_id === 'string' ? payload.run_id : null;
      if (r) {
        finishedRuns.add(r);
        if ((payload as any).resolution_emitted === true) {
          modernTerminalRuns.add(r);
        }
      }
    }
  }
  // Legacy runs · finished but never resolved by modern audits AND not
  // marked as modern terminal. Pre-2026-05-12 runs land here · modern
  // runs whose resolution WS broadcast got lost are protected by the
  // modernTerminalRuns marker.
  const legacyFinishedRuns = new Set<string>();
  for (const r of finishedRuns) {
    if (!resolvedRuns.has(r) && !modernTerminalRuns.has(r)) legacyFinishedRuns.add(r);
  }

  for (const evt of sorted) {
    const orderId = eventOrder(evt);
    if (orderId === null) continue;
    const auditMarker = eventAuditMarker(evt, orderId);
    if (workspaceId && evt.workspace_id !== workspaceId) continue;
    const payload = evt.payload ?? {};

    if (evt.kind === 'canvas.create_node') {
      const node_id = typeof payload.node_id === 'string' ? payload.node_id : null;
      if (!node_id) continue;
      const node_kind = typeof payload.node_kind === 'string' && VALID_KINDS.has(payload.node_kind as NodeKind)
        ? payload.node_kind as NodeKind
        : 'task';
      const status = typeof payload.status === 'string' && VALID_STATUSES.has(payload.status as NodeStatus)
        ? payload.status as NodeStatus
        : 'dormant';
      const meta = (payload as any).meta ?? {};
      const initData = objectField((payload as any).init_data);
      const title = textField((payload as any).title) ?? textField(initData.title) ?? textField(meta.title);
      const description = textField((payload as any).description) ?? textField(initData.description) ?? textField(meta.description);
      const assigned_agent_id = textField((payload as any).assigned_agent_id) ?? textField(initData.assigned_agent_id) ?? textField(meta.assigned_agent_id);
      const planner_key = textField((payload as any).planner_key) ?? textField(initData.planner_key) ?? textField(meta.planner_key);
      const source_audit_start = intField((payload as any).source_audit_start) ?? intField(initData.source_audit_start) ?? intField(meta.source_audit_start);
      const source_audit_end = intField((payload as any).source_audit_end) ?? intField(initData.source_audit_end) ?? intField(meta.source_audit_end);
      const plan_revision = intField((payload as any).plan_revision) ?? intField(initData.plan_revision) ?? intField(meta.plan_revision);
      const superseded_at = intField((payload as any).superseded_at) ?? intField(initData.superseded_at) ?? intField(meta.superseded_at);
      const acceptance_criteria = stringArrayField((payload as any).acceptance_criteria)
        ?? stringArrayField(initData.acceptance_criteria)
        ?? stringArrayField(meta.acceptance_criteria)
        ?? [];
      const context_refs = arrayField((payload as any).context_refs)
        ?? arrayField(initData.context_refs)
        ?? arrayField(meta.context_refs)
        ?? [];
      const boundaries = (payload as any).boundaries ?? initData.boundaries ?? meta.boundaries ?? null;
      const branches = branchesField((payload as any).branches)
        ?? branchesField(initData.branches)
        ?? branchesField(meta.branches)
        ?? [];
      const merge = mergeField((payload as any).merge)
        ?? mergeField(objectField(boundaries).merge)
        ?? mergeField(initData.merge)
        ?? mergeField(meta.merge);
      const rawPx = (payload as any).position_x;
      const rawPy = (payload as any).position_y;
      const px = typeof rawPx === 'number' && Number.isFinite(rawPx) ? rawPx : 0;
      const py = typeof rawPy === 'number' && Number.isFinite(rawPy) ? rawPy : 0;
      if (!nodes.has(node_id)) {
        nodes.set(node_id, {
          node_id,
          node_kind,
          status,
          title,
          description,
          assigned_agent_id,
          planner_key,
          source_audit_start,
          source_audit_end,
          plan_revision,
          superseded_at,
          acceptance_criteria,
          context_refs,
          boundaries,
          branches,
          merge,
          created_audit_id: auditMarker,
          last_audit_id: auditMarker,
          position_x: px,
          position_y: py,
        });
      }
      continue;
    }

    if (evt.kind === 'workflow.patch_applied' || evt.kind === 'workflow.snapshot') {
      const patchNodes = Array.isArray((payload as any).nodes) ? (payload as any).nodes : [];
      for (const rawNode of patchNodes) {
        const n = objectField(rawNode);
        const node_id = textField(n.node_id);
        if (!node_id) continue;
        const existing = nodes.get(node_id);
        if (existing && orderId <= existing.last_audit_id) continue;
        const node_kind = typeof n.node_kind === 'string' && VALID_KINDS.has(n.node_kind as NodeKind)
          ? n.node_kind as NodeKind
          : existing?.node_kind ?? 'task';
        const status = typeof n.status === 'string' && VALID_STATUSES.has(n.status as NodeStatus)
          ? n.status as NodeStatus
          : existing?.status ?? 'dormant';
        nodes.set(node_id, {
          node_id,
          node_kind,
          status,
          title: textField(n.title) ?? existing?.title ?? null,
          description: textField(n.description) ?? existing?.description ?? null,
          assigned_agent_id: textField(n.assigned_agent_id) ?? existing?.assigned_agent_id ?? null,
          planner_key: textField(n.planner_key) ?? existing?.planner_key ?? null,
          source_audit_start: intField(n.source_audit_start) ?? existing?.source_audit_start ?? null,
          source_audit_end: intField(n.source_audit_end) ?? existing?.source_audit_end ?? null,
          plan_revision: intField(n.plan_revision) ?? existing?.plan_revision ?? null,
          superseded_at: intField(n.superseded_at) ?? existing?.superseded_at ?? null,
          acceptance_criteria: stringArrayField(n.acceptance_criteria) ?? existing?.acceptance_criteria ?? [],
          context_refs: arrayField(n.context_refs) ?? existing?.context_refs ?? [],
          boundaries: n.boundaries ?? existing?.boundaries ?? null,
          branches: branchesField(n.branches) ?? existing?.branches ?? [],
          merge: mergeField(n.merge) ?? mergeField(objectField(n.boundaries).merge) ?? existing?.merge ?? null,
          created_audit_id: existing?.created_audit_id ?? auditMarker,
          last_audit_id: auditMarker,
          position_x: typeof n.position_x === 'number' && Number.isFinite(n.position_x) ? n.position_x : existing?.position_x ?? 0,
          position_y: typeof n.position_y === 'number' && Number.isFinite(n.position_y) ? n.position_y : existing?.position_y ?? 0,
        });
      }

      const supersededAt = intField((payload as any).superseded_at)
        ?? (typeof evt.ts === 'number' && Number.isSafeInteger(evt.ts) ? evt.ts : orderId);
      for (const node_id of stringArrayField((payload as any).supersede_node_ids) ?? []) {
        const existing = nodes.get(node_id);
        if (!existing || orderId <= existing.last_audit_id) continue;
        nodes.set(node_id, {
          ...existing,
          status: 'excluded',
          superseded_at: supersededAt,
          plan_revision: intField((payload as any).new_revision) ?? existing.plan_revision,
          last_audit_id: auditMarker,
        });
      }
      continue;
    }

    // P3-deploy 2026-05-11 · backend post-hoc cleanup of chat-only task_runs.
    // When agent only invoked mcp__aiw_chat__* tools (or none) · backend
    // deletes the auto-created node + node_run and broadcasts 'node.cleanup'.
    // SPA reducer removes the node from the canvas Map so it disappears
    // visually. See task-runs.ts isCanvasWorthy().
    if (evt.kind === 'node.cleanup') {
      const node_id = typeof payload.node_id === 'string' ? payload.node_id : null;
      if (node_id) {
        nodes.delete(node_id);
      }
      // Also clear any run→node mappings pointing at the dead node so future
      // task_run.* events for the run get dropped (line 347 'orphan' check).
      const run_id = typeof payload.run_id === 'string' ? payload.run_id : null;
      if (run_id) {
        runIdToNodeId.delete(run_id);
      }
      continue;
    }

    if (evt.kind === 'canvas.update_node_status') {
      const node_id = typeof payload.node_id === 'string' ? payload.node_id : null;
      if (!node_id) continue;
      const existing = nodes.get(node_id);
      if (!existing) continue; // update before create · drop
      // codex-review 2026-04-29 P2.6.c R1 P1 fix · backend writes `to_status`
      // (and from_status) per agent-tools.ts:293 · we read both for forward
      // compat.
      const rawStatus = typeof payload.to_status === 'string'
        ? payload.to_status
        : (typeof payload.status === 'string' ? payload.status : null);
      const status = rawStatus && VALID_STATUSES.has(rawStatus as NodeStatus)
        ? rawStatus as NodeStatus
        : existing.status;
      if (orderId > existing.last_audit_id) {
        nodes.set(node_id, {
          ...existing,
          status,
          last_audit_id: auditMarker,
        });
      }
      continue;
    }

    if (evt.kind === 'canvas.update_node') {
      const node_id = typeof payload.node_id === 'string' ? payload.node_id : null;
      if (!node_id) continue;
      const existing = nodes.get(node_id);
      if (!existing || orderId <= existing.last_audit_id) continue;
      const set = objectField((payload as any).set);
      const rawStatus = textField((payload as any).status) ?? textField(set.status);
      const status = rawStatus && VALID_STATUSES.has(rawStatus as NodeStatus)
        ? rawStatus as NodeStatus
        : existing.status;
      nodes.set(node_id, {
        ...existing,
        status,
        title: textField((payload as any).title) ?? textField(set.title) ?? existing.title,
        description: textField((payload as any).description) ?? textField(set.description) ?? existing.description,
        assigned_agent_id: textField((payload as any).assigned_agent_id) ?? textField(set.assigned_agent_id) ?? existing.assigned_agent_id,
        last_audit_id: auditMarker,
      });
      continue;
    }

    // P3 P1-6 task_run.* lifecycle (codex R1+R2 fixes integrated single-pass).
    //
    // P3-deploy 2026-05-11 · pre-emptive canvas separation. The modern
    // path records the run→node mapping but does NOT auto-create a node ·
    // the canvas node only appears when the agent explicitly calls
    // mcp__aiw_canvas__create_node OR backend backfills it after /finish.
    //
    // Legacy gap-fill exception (codex P1 #5) · auto-create for runs that
    // finished pre-2026-05-11 (terminal event seen but no node.cleanup or
    // backfill audit). Without this · refresh / gap-fill drops historical
    // canvas nodes.
    if (evt.kind === 'task_run.created') {
      const run_id = typeof payload.run_id === 'string' ? payload.run_id : null;
      const node_id = typeof payload.node_id === 'string' ? payload.node_id : null;
      if (run_id && node_id && !runIdToNodeId.has(run_id)) {
        runIdToNodeId.set(run_id, node_id);
        if (legacyFinishedRuns.has(run_id) && !nodes.has(node_id)) {
          nodes.set(node_id, {
            node_id,
            node_kind: 'task',
            status: 'dormant',
            title: null, // legacy runs · no title source · fallback to hash slice
            description: null,
            assigned_agent_id: null,
            planner_key: null,
            source_audit_start: null,
            source_audit_end: null,
            plan_revision: null,
            superseded_at: null,
            acceptance_criteria: [],
            context_refs: [],
            boundaries: null,
            created_audit_id: auditMarker,
            last_audit_id: auditMarker,
            position_x: 0,
            position_y: 0,
          });
        }
      }
      continue;
    }

    if (typeof evt.kind !== 'string' || !evt.kind.startsWith('task_run.')) continue;
    const run_id = typeof payload.run_id === 'string' ? payload.run_id : null;
    if (!run_id) continue;
    const node_id = runIdToNodeId.get(run_id);
    if (!node_id) continue;
    const existing = nodes.get(node_id);
    if (!existing) continue;
    if (orderId <= existing.last_audit_id) continue;

    // Map lifecycle event → canvas node status (CLAUDE.md rule 4 · 8 states).
    // queued cancel/interrupt with dispatched===false · terminal. Kept for
    // old audit streams that predate the canonical task_run.canceled follow-up.
    const dispatchedFalse = payload.dispatched === false;
    const nextStatus: NodeStatus | null =
      evt.kind === 'task_run.dispatched' ? 'running' :
      evt.kind === 'task_run.queued_busy' ? 'waiting' :
      evt.kind === 'task_run.waiting_worker' ? 'waiting' :
      evt.kind === 'task_run.waiting_tree' ? 'waiting' :
      evt.kind === 'task_run.finished' ? 'done' :
      evt.kind === 'task_run.error' ? 'blocked' :
      evt.kind === 'task_run.canceled' ? 'excluded' :
      evt.kind === 'task_run.skipped' ? 'excluded' :
      ((evt.kind === 'task_run.cancel_requested' || evt.kind === 'task_run.interrupt_requested')
        && dispatchedFalse) ? 'excluded' :
      null;
    if (!nextStatus) continue;
    nodes.set(node_id, {
      ...existing,
      status: nextStatus,
      last_audit_id: auditMarker,
    });
  }

  return [...nodes.values()].sort((a, b) => a.created_audit_id - b.created_audit_id);
}

/**
 * Extract canvas edges from the audit stream. Pure · same input contract as
 * `eventsToNodes`. Skips edges whose endpoints don't appear in the visible
 * node set (e.g. after a `node.cleanup` removed one side · or the edge
 * payload is malformed).
 *
 * v0.1 user-feedback 2026-05-12 · canvas is a task tree · most edges are
 * `flow` parent→child created atomically by `canvas.create_node` with
 * `parent_node_id`. Cross-tree links use the standalone `canvas.create_edge`.
 */
export function eventsToEdges(events: WsEvent[], workspaceId?: string): CanvasEdge[] {
  // Pre-pass · which node_ids exist in the visible set? Edges to deleted /
  // cleaned-up nodes shouldn't render as dangling lines.
  const aliveNodeIds = new Set<string>();
  const sorted = [...events].sort((a, b) => (eventOrder(a) ?? 0) - (eventOrder(b) ?? 0));
  for (const e of sorted) {
    if (workspaceId && e.workspace_id !== workspaceId) continue;
    const p: any = e.payload ?? {};
    if (e.kind === 'canvas.create_node' && typeof p.node_id === 'string') {
      aliveNodeIds.add(p.node_id);
    } else if ((e.kind === 'workflow.patch_applied' || e.kind === 'workflow.snapshot') && Array.isArray(p.nodes)) {
      for (const rawNode of p.nodes) {
        const node = objectField(rawNode);
        const nodeId = textField(node.node_id);
        if (nodeId) aliveNodeIds.add(nodeId);
      }
    } else if (e.kind === 'node.cleanup' && typeof p.node_id === 'string') {
      aliveNodeIds.delete(p.node_id);
    }
  }

  const edges = new Map<string, CanvasEdge>();
  for (const evt of sorted) {
    if (workspaceId && evt.workspace_id !== workspaceId) continue;
    const orderId = eventOrder(evt);
    if (orderId === null) continue;
    const auditMarker = eventAuditMarker(evt, orderId);

    const upsertEdge = (p: Record<string, unknown>, replace: boolean) => {
      const edgeId = textField(p.edge_id);
      const from = textField(p.from_node_id);
      const to = textField(p.to_node_id);
      const kind = typeof p.edge_kind === 'string' && VALID_EDGE_KINDS.has(p.edge_kind) ? p.edge_kind : null;
      if (!edgeId || !from || !to || !kind) return;
      if (!aliveNodeIds.has(from) || !aliveNodeIds.has(to)) return;
      const prior = edges.get(edgeId);
      if (prior && !replace) return;
      edges.set(edgeId, {
        edge_id: edgeId,
        from_node_id: from,
        to_node_id: to,
        edge_kind: kind as 'flow' | 'data' | 'dependency',
        created_audit_id: prior?.created_audit_id ?? auditMarker,
      });
    };

    const p: any = evt.payload ?? {};
    if (evt.kind === 'canvas.create_edge') {
      upsertEdge(p, false);
    } else if (evt.kind === 'canvas.delete_edge') {
      const edgeId = textField(p.edge_id);
      if (edgeId) {
        edges.delete(edgeId);
      } else {
        const from = textField(p.from_node_id);
        const to = textField(p.to_node_id);
        const kind = textField(p.edge_kind);
        for (const [id, edge] of edges) {
          if (edge.from_node_id === from && edge.to_node_id === to && edge.edge_kind === kind) {
            edges.delete(id);
          }
        }
      }
    } else if (evt.kind === 'workflow.snapshot' && Array.isArray(p.edges)) {
      const snapshotHasNodes = Array.isArray(p.nodes) && p.nodes.length > 0;
      const snapshotRevision = intField(p.workflow_plan_revision);
      if (snapshotHasNodes || (snapshotRevision !== null && snapshotRevision > 0)) {
        edges.clear();
      }
      for (const rawEdge of p.edges) {
        upsertEdge(objectField(rawEdge), true);
      }
    } else if (evt.kind === 'workflow.patch_applied' && Array.isArray(p.edges)) {
      for (const rawEdge of p.edges) {
        upsertEdge(objectField(rawEdge), true);
      }
    }
  }
  return [...edges.values()].sort((a, b) => a.created_audit_id - b.created_audit_id);
}

function impactMarkKey(input: {
  source_kind: string;
  source_id: string;
  affected_node_id: string;
}): string {
  return `${input.source_kind}:${input.source_id}:${input.affected_node_id}`;
}

function parseImpactStatus(value: unknown): CanvasImpactStatus | null {
  return typeof value === 'string' && VALID_IMPACT_STATUSES.has(value)
    ? value as CanvasImpactStatus
    : null;
}

function parseImpactMark(
  raw: unknown,
  fallbackSource: { kind?: string | null; id?: string | null; node_id?: string | null; run_id?: string | null } | null,
  auditMarker: number,
): CanvasImpactMark | null {
  const mark = objectField(raw);
  const sourceKind = textField(mark.source_kind) ?? textField(fallbackSource?.kind);
  const sourceId = textField(mark.source_id) ?? textField(fallbackSource?.id);
  const affectedNodeId = textField(mark.affected_node_id);
  const impactKind = textField(mark.impact_kind);
  const impactStatus = parseImpactStatus(mark.impact_status) ?? 'stale';
  const depth = intField(mark.depth);
  if (!sourceKind || !VALID_IMPACT_SOURCE_KINDS.has(sourceKind)) return null;
  if (!sourceId || !affectedNodeId) return null;
  if (!impactKind || !VALID_IMPACT_KINDS.has(impactKind)) return null;
  if (depth === null || depth < 1 || depth > 16) return null;
  const causeChain = recordArrayField(mark.cause_chain);
  return {
    source_kind: sourceKind as CanvasImpactSourceKind,
    source_id: sourceId,
    source_node_id: textField(mark.source_node_id) ?? textField(fallbackSource?.node_id),
    source_run_id: textField(mark.source_run_id) ?? textField(fallbackSource?.run_id),
    affected_node_id: affectedNodeId,
    impact_kind: impactKind as CanvasImpactKind,
    impact_status: impactStatus,
    depth,
    cause_chain: causeChain.length > 0 ? causeChain : recordArrayField(mark.cause_chain_json),
    created_at: intField(mark.created_at),
    updated_at: intField(mark.updated_at),
    last_audit_id: auditMarker,
  };
}

/**
 * Reduce workflow snapshots + live impact audits into current impact marks.
 * This keeps impact metadata separate from `nodes.status`: preview actions
 * can acknowledge or queue reruns without corrupting the P4 8-state model.
 */
export function eventsToImpactMarks(events: WsEvent[], workspaceId?: string): CanvasImpactMark[] {
  const marks = new Map<string, CanvasImpactMark>();
  const sorted = [...events].sort((a, b) => (eventOrder(a) ?? 0) - (eventOrder(b) ?? 0));

  for (const evt of sorted) {
    const orderId = eventOrder(evt);
    if (orderId === null) continue;
    if (workspaceId && evt.workspace_id !== workspaceId) continue;
    const auditMarker = eventAuditMarker(evt, orderId);
    const payload = objectField(evt.payload);

    if (evt.kind === 'workflow.snapshot') {
      const snapshotMarks = arrayField(payload.node_impact_marks);
      if (!snapshotMarks) continue;
      marks.clear();
      for (const rawMark of snapshotMarks) {
        const mark = parseImpactMark(rawMark, null, auditMarker);
        if (!mark) continue;
        marks.set(impactMarkKey(mark), mark);
      }
      continue;
    }

    if (evt.kind === 'node.impact_marked') {
      const source = objectField(payload.source);
      const fallbackSource = {
        kind: textField(source.kind),
        id: textField(source.id),
        node_id: textField(source.node_id),
        run_id: textField(source.run_id),
      };
      for (const rawMark of arrayField(payload.marks) ?? []) {
        const mark = parseImpactMark(rawMark, fallbackSource, auditMarker);
        if (!mark) continue;
        marks.set(impactMarkKey(mark), mark);
      }
      continue;
    }

    if (evt.kind === 'node.impact_action') {
      const source = objectField(payload.source);
      const sourceKind = textField(source.kind);
      const sourceId = textField(source.id);
      const nextStatus = parseImpactStatus(payload.updated_mark_status);
      if (!sourceKind || !sourceId || !nextStatus) continue;
      const selectedNodeIds = stringArrayField(payload.selected_node_ids) ?? [];
      const rerunNodeIds = stringArrayField(payload.rerun_node_ids) ?? [];
      const affectedNodeIds = nextStatus === 'rerun_queued' ? rerunNodeIds : selectedNodeIds;
      for (const affectedNodeId of affectedNodeIds) {
        const key = impactMarkKey({ source_kind: sourceKind, source_id: sourceId, affected_node_id: affectedNodeId });
        const existing = marks.get(key);
        if (!existing) continue;
        marks.set(key, {
          ...existing,
          impact_status: nextStatus,
          updated_at: typeof evt.ts === 'number' && Number.isFinite(evt.ts) ? evt.ts : existing.updated_at,
          last_audit_id: auditMarker,
        });
      }
    }
  }

  return [...marks.values()].sort((a, b) => {
    return (b.updated_at ?? 0) - (a.updated_at ?? 0)
      || a.depth - b.depth
      || a.affected_node_id.localeCompare(b.affected_node_id);
  });
}

export function impactSummaryByNode(marks: CanvasImpactMark[]): Map<string, CanvasImpactNodeSummary> {
  const activeMarks = marks.filter(isActiveImpactMark);
  const byNode = new Map<string, CanvasImpactNodeSummary>();
  for (const mark of activeMarks) {
    const existing = byNode.get(mark.affected_node_id) ?? {
      active_count: 0,
      direct_count: 0,
      indirect_count: 0,
      dirty_count: 0,
      stale_count: 0,
      rerun_queued_count: 0,
      highest_status: mark.impact_status,
    };
    existing.active_count += 1;
    if (mark.impact_kind === 'direct') existing.direct_count += 1;
    else existing.indirect_count += 1;
    if (mark.impact_status === 'dirty') existing.dirty_count += 1;
    if (mark.impact_status === 'stale') existing.stale_count += 1;
    if (mark.impact_status === 'rerun_queued') existing.rerun_queued_count += 1;
    if (existing.highest_status !== 'dirty') {
      existing.highest_status = mark.impact_status === 'dirty'
        ? 'dirty'
        : existing.highest_status === 'rerun_queued'
          ? 'rerun_queued'
          : mark.impact_status;
    }
    byNode.set(mark.affected_node_id, existing);
  }
  return byNode;
}

function isActiveImpactMark(mark: CanvasImpactMark): boolean {
  return mark.impact_status !== 'acknowledged' && mark.impact_status !== 'resolved';
}

export async function applyImpactAction(opts: {
  backendUrl: string;
  authToken: string;
  sessionId: string;
  sourceKind: CanvasImpactSourceKind;
  sourceId: string;
  action: CanvasImpactAction;
  affectedNodeIds?: string[];
  budgetNodeLimit?: number;
  maxDepth?: number;
  note?: string;
  fetchImpl?: typeof fetch;
}): Promise<CanvasImpactActionResult> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  const resp = await fetchImpl(`${opts.backendUrl.replace(/\/$/, '')}/api/sessions/${encodeURIComponent(opts.sessionId)}/impact/action`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${opts.authToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      source_kind: opts.sourceKind,
      source_id: opts.sourceId,
      action: opts.action,
      affected_node_ids: opts.affectedNodeIds,
      budget_node_limit: opts.budgetNodeLimit,
      max_depth: opts.maxDepth,
      note: opts.note,
    }),
  });
  let body: any = {};
  try {
    body = await resp.json();
  } catch {
    body = {};
  }
  if (!resp.ok) {
    return {
      ok: false,
      error: body.error ?? `HTTP ${resp.status}`,
      error_code: body.error_code,
    };
  }
  return body as CanvasImpactActionResult;
}

/**
 * Compute final on-screen positions for nodes · honoring backend-assigned
 * `position_x`/`position_y` when present · falling back to a deterministic
 * grid when every node sits at (0,0) (legacy nodes from before backend started
 * assigning positions · or agent-created nodes without parent_node_id).
 *
 * Returns a map of node_id → {x, y, width, height} used by the renderer for
 * absolute positioning of node cards AND for the SVG line endpoints.
 */
export interface NodeLayout { x: number; y: number; width: number; height: number; }
const NODE_W = 200;
const NODE_H = 90;
const FALLBACK_GRID_GAP_X = 240;
const FALLBACK_GRID_GAP_Y = 120;
const FALLBACK_GRID_COLS = 4;

export function layoutNodes(nodes: CanvasNode[]): Map<string, NodeLayout> {
  const out = new Map<string, NodeLayout>();
  const allZero = nodes.every((n) => n.position_x === 0 && n.position_y === 0);
  if (allZero && nodes.length > 1) {
    // Fallback grid · stable order by created_audit_id (already sorted).
    nodes.forEach((n, i) => {
      const col = i % FALLBACK_GRID_COLS;
      const row = Math.floor(i / FALLBACK_GRID_COLS);
      out.set(n.node_id, {
        x: 24 + col * FALLBACK_GRID_GAP_X,
        y: 24 + row * FALLBACK_GRID_GAP_Y,
        width: NODE_W,
        height: NODE_H,
      });
    });
    return out;
  }
  // Honor backend positions · shift so leftmost / topmost sits at canvas padding.
  let minX = Infinity, minY = Infinity;
  for (const n of nodes) {
    if (n.position_x < minX) minX = n.position_x;
    if (n.position_y < minY) minY = n.position_y;
  }
  if (!Number.isFinite(minX)) minX = 0;
  if (!Number.isFinite(minY)) minY = 0;
  for (const n of nodes) {
    out.set(n.node_id, {
      x: 24 + (n.position_x - minX),
      y: 24 + (n.position_y - minY),
      width: NODE_W,
      height: NODE_H,
    });
  }
  return out;
}

/**
 * Format byte count → human string. Pure · exported for tests.
 *
 * Choices:
 *   - 1 decimal place once we leave bytes · KB/MB/GB feel right at one digit
 *   - binary units (1024) · matches how R2 reports object size + matches what
 *     most engineers expect when staring at a build artifact
 *   - returns '?' for NaN / Infinity / negative · so a malformed event doesn't
 *     crash render with NaN · we'd rather show '?' than blow up the canvas
 */
export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '?';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

export interface PresignArtifactDownloadOpts {
  backendUrl: string;
  authToken: string;
  hash: string;
  /** Original filename · backend signs into response-content-disposition so
   *  download shows the right name + browser picks the right encoding. */
  filename?: string;
  fetchImpl?: typeof fetch;
}
export interface PresignArtifactDownloadResult {
  ok: boolean;
  url?: string;
  error?: string;
}

/**
 * Call backend to mint a short-lived presigned GET for an artifact blob.
 *
 * Pure async function · exported for unit testing. Caller decides how to use
 * the URL (window.open · <a download> · fetch + Blob etc).
 *
 * Why not have the SPA hit R2 directly: R2 GETs require sigv4 + the signing
 * secret stays in the Worker · the Worker is also where workspace ownership
 * is verified (blob_refs row · A18). So the SPA always asks the backend
 * "give me a signed URL for this hash" and trusts the resulting URL.
 *
 * Plan note (P2.8.D1): "下载需要 SPA token 调 /api/blob/presign-get?hash=...
 * · 不要只拼 R2 URL"。
 */
export async function presignArtifactDownload(
  opts: PresignArtifactDownloadOpts,
): Promise<PresignArtifactDownloadResult> {
  const fi = opts.fetchImpl ?? globalThis.fetch;
  if (!/^[a-f0-9]{64}$/.test(opts.hash)) {
    return { ok: false, error: 'invalid hash' };
  }
  const base = opts.backendUrl.replace(/\/$/, '');
  // Pass filename so backend signs in response-content-disposition · the
  // browser downloads with the original name (notes.md instead of <hash>)
  // and renders UTF-8 correctly (no Latin-1 fallback that mangles 中文).
  const filenameParam = opts.filename ? `&filename=${encodeURIComponent(opts.filename)}` : '';
  const url = `${base}/api/blob/presign-get?hash=${encodeURIComponent(opts.hash)}${filenameParam}`;
  let resp: Response;
  try {
    resp = await fi(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${opts.authToken}` },
    });
  } catch (e: any) {
    return { ok: false, error: String(e?.message ?? e) };
  }
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const err = await resp.json() as { error?: string };
      detail = err.error ?? detail;
    } catch { /* ignore non-JSON error body */ }
    return { ok: false, error: detail };
  }
  try {
    const body = await resp.json() as { url?: string };
    if (!body.url || typeof body.url !== 'string') {
      return { ok: false, error: 'no url in response' };
    }
    return { ok: true, url: body.url };
  } catch (e: any) {
    return { ok: false, error: `non-JSON response: ${String(e?.message ?? e)}` };
  }
}

// Status colors moved to sketch.css `.node.{status}` classes (matches design).

const STATUS_LABEL: Record<NodeStatus, string> = {
  dormant: '休眠',
  running: '运行中',
  thinking: '思考中',
  waiting: '等待',
  blocked: '阻塞',
  done: '完成',
  excluded: '排除',
  dirty: '脏数据',
};

const KIND_LABEL: Record<NodeKind, string> = {
  task: '任务',
  decision: '决策',
  human: '人类',
  output: '产出',
};

// v0.1 user-feedback 2026-05-12 round 2 · switched hand-rolled SVG render
// to React Flow + dagre. Hand-routed bezier edges couldn't avoid overlap /
// crossing nodes. dagre gives Sugiyama layout + ReactFlow handles edge
// routing professionally (anchored to node handles · auto-curves).

type AiwNodeData = {
  canvasNode: CanvasNode;
  selected: boolean;
  relation?: CanvasNodeRelation;
  impact?: CanvasImpactNodeSummary;
  onSelect?: (id: string) => void;
} & Record<string, unknown>;

type AiwRFNode = RFNode<AiwNodeData, 'aiwTask'>;

export type CanvasNodeRelation = 'selected' | 'upstream' | 'downstream' | 'unrelated';
export type CanvasEdgeRelation = 'upstream' | 'downstream' | 'unrelated';

export interface SelectedGraphRelations {
  nodeRelations: Map<string, CanvasNodeRelation>;
  edgeRelations: Map<string, CanvasEdgeRelation>;
}

interface DagreLaidOut {
  nodes: AiwRFNode[];
  edges: RFEdge[];
}

const RF_NODE_WIDTH = 220;
const RF_NODE_HEIGHT = 92;
export const CANVAS_LARGE_GRAPH_THRESHOLD = 2000;
const LARGE_GRAPH_GROUP_LIMIT = 24;
const LARGE_GRAPH_ANCHOR_LIMIT = 18;

interface LargeGraphGroup {
  key: string;
  label: string;
  nodeCount: number;
  edgeCount: number;
  statusCounts: Partial<Record<NodeStatus, number>>;
}

interface LargeGraphSummary {
  totalNodes: number;
  totalEdges: number;
  flowEdges: number;
  dependencyEdges: number;
  dataEdges: number;
  groupCount: number;
  groups: LargeGraphGroup[];
  anchors: CanvasNode[];
}

function groupKeyForNode(node: CanvasNode): string {
  const key = node.planner_key ?? node.node_id;
  const round = key.match(/:r(\d{2,4})/);
  if (round) return `r${round[1]}`;
  return 'legacy';
}

function groupLabel(key: string): string {
  if (key === 'legacy') return '未分组';
  return `第 ${Number(key.slice(1))} 轮`;
}

function edgeGroupKey(edge: CanvasEdge, nodeGroupById: Map<string, string>): string {
  return nodeGroupById.get(edge.to_node_id) ?? nodeGroupById.get(edge.from_node_id) ?? 'legacy';
}

function pickLargeGraphAnchors(nodes: CanvasNode[]): CanvasNode[] {
  const byId = new Map(nodes.map((n) => [n.node_id, n]));
  const anchors = new Map<string, CanvasNode>();
  const add = (node: CanvasNode | undefined) => {
    if (node) anchors.set(node.node_id, node);
  };

  add(nodes.find((n) => n.planner_key?.includes(':r01') && n.planner_key?.includes(':task:brief')));
  add([...nodes].reverse().find((n) => n.planner_key?.includes(':s01:decision:path')));
  add([...nodes].reverse().find((n) => n.node_kind === 'decision'));
  add(nodes.find((n) => n.planner_key?.endsWith(':output:global-release') || n.title === '全局上线交付汇总'));
  add([...nodes].reverse().find((n) => n.node_kind === 'output'));
  add(nodes.find((n) => n.node_kind === 'human'));

  const stride = Math.max(1, Math.floor(nodes.length / Math.max(1, LARGE_GRAPH_ANCHOR_LIMIT - anchors.size)));
  for (let i = 0; i < nodes.length && anchors.size < LARGE_GRAPH_ANCHOR_LIMIT; i += stride) {
    add(byId.get(nodes[i].node_id));
  }
  return [...anchors.values()].slice(0, LARGE_GRAPH_ANCHOR_LIMIT);
}

export function summarizeLargeGraph(nodes: CanvasNode[], edges: CanvasEdge[]): LargeGraphSummary {
  const nodeGroupById = new Map<string, string>();
  const groupMap = new Map<string, LargeGraphGroup>();

  for (const node of nodes) {
    const key = groupKeyForNode(node);
    nodeGroupById.set(node.node_id, key);
    const group = groupMap.get(key) ?? {
      key,
      label: groupLabel(key),
      nodeCount: 0,
      edgeCount: 0,
      statusCounts: {},
    };
    group.nodeCount += 1;
    group.statusCounts[node.status] = (group.statusCounts[node.status] ?? 0) + 1;
    groupMap.set(key, group);
  }

  let flowEdges = 0;
  let dependencyEdges = 0;
  let dataEdges = 0;
  for (const edge of edges) {
    if (edge.edge_kind === 'flow') flowEdges += 1;
    else if (edge.edge_kind === 'dependency') dependencyEdges += 1;
    else dataEdges += 1;
    const key = edgeGroupKey(edge, nodeGroupById);
    const group = groupMap.get(key);
    if (group) group.edgeCount += 1;
  }

  const groups = [...groupMap.values()]
    .sort((a, b) => {
      if (a.key === 'legacy') return 1;
      if (b.key === 'legacy') return -1;
      return a.key.localeCompare(b.key);
    })
    .slice(0, LARGE_GRAPH_GROUP_LIMIT);

  return {
    totalNodes: nodes.length,
    totalEdges: edges.length,
    flowEdges,
    dependencyEdges,
    dataEdges,
    groupCount: groupMap.size,
    groups,
    anchors: pickLargeGraphAnchors(nodes),
  };
}

export function selectedGraphRelations(
  canvasNodes: CanvasNode[],
  canvasEdges: CanvasEdge[],
  selectedNodeId: string | null | undefined,
): SelectedGraphRelations {
  const nodeIds = new Set(canvasNodes.map((n) => n.node_id));
  const nodeRelations = new Map<string, CanvasNodeRelation>();
  const edgeRelations = new Map<string, CanvasEdgeRelation>();
  for (const nodeId of nodeIds) nodeRelations.set(nodeId, 'unrelated');
  for (const edge of canvasEdges) edgeRelations.set(edge.edge_id, 'unrelated');
  if (!selectedNodeId || !nodeIds.has(selectedNodeId)) {
    return { nodeRelations, edgeRelations };
  }

  const incoming = new Map<string, string[]>();
  const outgoing = new Map<string, string[]>();
  for (const edge of canvasEdges) {
    if (!nodeIds.has(edge.from_node_id) || !nodeIds.has(edge.to_node_id)) continue;
    incoming.set(edge.to_node_id, [...(incoming.get(edge.to_node_id) ?? []), edge.from_node_id]);
    outgoing.set(edge.from_node_id, [...(outgoing.get(edge.from_node_id) ?? []), edge.to_node_id]);
  }

  const walk = (start: string, graph: Map<string, string[]>): Set<string> => {
    const seen = new Set<string>();
    const stack = [...(graph.get(start) ?? [])];
    while (stack.length > 0) {
      const next = stack.pop()!;
      if (seen.has(next) || next === start) continue;
      seen.add(next);
      for (const child of graph.get(next) ?? []) stack.push(child);
    }
    return seen;
  };

  const upstream = walk(selectedNodeId, incoming);
  const downstream = walk(selectedNodeId, outgoing);

  for (const nodeId of upstream) nodeRelations.set(nodeId, 'upstream');
  for (const nodeId of downstream) {
    if (!upstream.has(nodeId)) nodeRelations.set(nodeId, 'downstream');
  }
  nodeRelations.set(selectedNodeId, 'selected');

  for (const edge of canvasEdges) {
    const upstreamEdge = upstream.has(edge.from_node_id) && (upstream.has(edge.to_node_id) || edge.to_node_id === selectedNodeId);
    const downstreamEdge = (edge.from_node_id === selectedNodeId || downstream.has(edge.from_node_id)) && downstream.has(edge.to_node_id);
    if (upstreamEdge) edgeRelations.set(edge.edge_id, 'upstream');
    else if (downstreamEdge) edgeRelations.set(edge.edge_id, 'downstream');
  }

  return { nodeRelations, edgeRelations };
}

/**
 * Run dagre Sugiyama layout · left-to-right · returns nodes/edges in
 * React Flow format with computed positions. Edges are tagged with edge_kind
 * so the renderer can pick stroke style.
 */
export function dagreLayout(
  canvasNodes: CanvasNode[],
  canvasEdges: CanvasEdge[],
  selectedNodeId: string | null | undefined,
  onSelect: ((id: string) => void) | undefined,
  impactByNode?: Map<string, CanvasImpactNodeSummary>,
): DagreLaidOut {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 28, ranksep: 80, marginx: 24, marginy: 24 });
  const relations = selectedGraphRelations(canvasNodes, canvasEdges, selectedNodeId);
  for (const n of canvasNodes) {
    g.setNode(n.node_id, { width: RF_NODE_WIDTH, height: RF_NODE_HEIGHT });
  }
  for (const e of canvasEdges) {
    g.setEdge(e.from_node_id, e.to_node_id, { weight: e.edge_kind === 'flow' ? 2 : 1 });
  }
  dagre.layout(g);

  const rfNodes: AiwRFNode[] = canvasNodes.map((n) => {
    const pos = g.node(n.node_id);
    return {
      id: n.node_id,
      type: 'aiwTask',
      position: pos ? { x: pos.x - RF_NODE_WIDTH / 2, y: pos.y - RF_NODE_HEIGHT / 2 } : { x: 0, y: 0 },
      data: {
        canvasNode: n,
        selected: selectedNodeId === n.node_id,
        relation: selectedNodeId ? (relations.nodeRelations.get(n.node_id) ?? 'unrelated') : undefined,
        impact: impactByNode?.get(n.node_id),
        onSelect,
      },
      draggable: false,
      selectable: false,
    };
  });

  const rfEdges = canvasEdges.map((e): RFEdge => {
    const isDep = e.edge_kind === 'dependency';
    const isData = e.edge_kind === 'data';
    const relation = relations.edgeRelations.get(e.edge_id) ?? 'unrelated';
    const hasSelection = Boolean(selectedNodeId);
    const highlighted = relation !== 'unrelated';
    return {
      id: e.edge_id,
      source: e.from_node_id,
      target: e.to_node_id,
      type: 'smoothstep',
      animated: isDep,
      style: {
        stroke: isDep
          ? 'var(--accent-warning, #f59e0b)'
          : isData
            ? 'var(--accent-primary, #3b82f6)'
            : 'var(--fg-faint, #94a3b8)',
        strokeWidth: highlighted ? 2.6 : 1.6,
        strokeDasharray: isDep ? '6 4' : undefined,
        opacity: highlighted ? 1 : hasSelection ? 0.28 : 0.85,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: isDep
          ? 'var(--accent-warning, #f59e0b)'
          : isData
            ? 'var(--accent-primary, #3b82f6)'
            : 'var(--fg-faint, #94a3b8)',
        width: 14,
        height: 14,
      },
      data: { edge_kind: e.edge_kind, relation },
    };
  });

  return { nodes: rfNodes, edges: rfEdges };
}

/** Custom ReactFlow node · renders our NodeCard inside RF positioning frame.
 *  v0.1 user-feedback 2026-05-12 round 5 · attach the click handler directly
 *  to the wrapper div with stopPropagation. RF's pane has `panOnDrag` which
 *  intercepts pointerdown for canvas panning · the onNodeClick prop fires
 *  in some scenarios but is unreliable in our setup (pan-blocked clicks
 *  never bubble up as a real click event). Handling at the node-component
 *  level guarantees the click registers · regardless of RF's pan logic. */
function AiwTaskNode({ data }: NodeProps<AiwRFNode>): JSX.Element {
  const { canvasNode, selected, relation, impact, onSelect } = data;
  return (
    <div
      style={{ width: RF_NODE_WIDTH, cursor: 'pointer' }}
      onPointerDown={(e) => {
        // Stop RF's pane pan/drag from swallowing this pointerdown.
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(canvasNode.node_id);
      }}
    >
      {/* Hidden handles · ReactFlow needs them on left/right to anchor edges */}
      <Handle type="target" position={Position.Left} style={{ opacity: 0, pointerEvents: 'none' }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0, pointerEvents: 'none' }} />
      <NodeCard node={canvasNode} selected={selected} relation={relation} impact={impact} onSelect={onSelect} />
    </div>
  );
}

function LargeGraphOverview({
  summary,
  selectedNodeId,
  onSelectNode,
  impactByNode,
}: {
  summary: LargeGraphSummary;
  selectedNodeId: string | null | undefined;
  onSelectNode?: (nodeId: string) => void;
  impactByNode: Map<string, CanvasImpactNodeSummary>;
}): JSX.Element {
  return (
    <div
      data-testid="canvas-large-graph-mode"
      style={{
        height: '100%',
        minHeight: 640,
        overflow: 'auto',
        padding: '18px 20px 22px',
        background: 'linear-gradient(180deg, var(--bg-base), var(--bg-canvas))',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <LargeGraphMetric testId="canvas-large-node-count" label="节点" value={summary.totalNodes} />
        <LargeGraphMetric testId="canvas-large-edge-count" label="连线" value={summary.totalEdges} />
        <LargeGraphMetric testId="canvas-large-group-count" label="轮次分组" value={summary.groupCount} />
        <LargeGraphMetric testId="canvas-large-anchor-count" label="可见锚点" value={summary.anchors.length} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 0.9fr) minmax(360px, 1.4fr)',
          gap: 14,
          alignItems: 'start',
        }}
      >
        <section style={largeGraphSectionStyle}>
          <div style={largeGraphSectionHeadStyle}>
            <Icon name="branch" size={14} />
            <strong>大图索引</strong>
          </div>
          <p style={largeGraphHintStyle}>
            当前 workflow 已进入大图模式：保留完整节点数据与详情联动，画布优先呈现轮次聚合和关键锚点，避免一次性挂载全量 DOM。
          </p>
          <div style={largeGraphEdgeLegendStyle}>
            <span>flow {summary.flowEdges}</span>
            <span>dependency {summary.dependencyEdges}</span>
            <span>data {summary.dataEdges}</span>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {summary.groups.map((group) => (
              <div
                key={group.key}
                data-testid="canvas-large-group-row"
                style={largeGraphGroupRowStyle}
              >
                <strong>{group.label}</strong>
                <span>{group.nodeCount} 节点</span>
                <span>{group.edgeCount} 连线</span>
                <span>{group.statusCounts.done ?? 0} 完成</span>
                <span>{group.statusCounts.waiting ?? 0} 等待</span>
              </div>
            ))}
          </div>
        </section>

        <section style={largeGraphSectionStyle}>
          <div style={largeGraphSectionHeadStyle}>
            <Icon name="agent" size={14} />
            <strong>关键锚点</strong>
          </div>
          <div
            data-testid="canvas-large-anchors"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12,
              alignItems: 'start',
            }}
          >
            {summary.anchors.map((node) => (
              <div key={node.node_id} data-testid={`canvas-large-anchor-${node.node_id}`}>
                <NodeCard
                  node={node}
                  selected={selectedNodeId === node.node_id}
                  impact={impactByNode.get(node.node_id)}
                  onSelect={onSelectNode}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function LargeGraphMetric({
  testId,
  label,
  value,
}: {
  testId: string;
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div style={largeGraphMetricStyle}>
      <span style={{ color: 'var(--fg-faint)', fontSize: 11, fontWeight: 700 }}>{label}</span>
      <strong data-testid={testId} style={{ fontSize: 22, lineHeight: 1.1 }}>{value.toLocaleString('en-US')}</strong>
    </div>
  );
}

const RF_NODE_TYPES = { aiwTask: AiwTaskNode };

export interface CanvasViewProps {
  sessionId: string;
  /** Optional workspace_id · enables paranoid cross-tenant filter in eventsToNodes. */
  workspaceId?: string;
  /**
   * Backend base URL (no trailing slash) · required for artifact downloads.
   * Optional so SSR / static render tests can omit. When missing the
   * download button stays disabled with a hint.
   */
  backendUrl?: string;
  /** SPA JWT · required for artifact downloads. Same omit-rule as backendUrl. */
  authToken?: string;
  /** P3 P0-4 · selected node id from parent (drives ws-panel detail view). */
  selectedNodeId?: string | null;
  /** P3 P0-4 · callback when user clicks a node card. */
  onSelectNode?: (nodeId: string) => void;
  /** Test-only · inject custom store. */
  storeForTest?: typeof useAuditStore;
  /** Test-only · inject fetch (jsdom env doesn't always have it). */
  fetchImplForTest?: typeof fetch;
}

export function CanvasView({
  sessionId,
  workspaceId,
  backendUrl,
  authToken,
  selectedNodeId,
  onSelectNode,
  storeForTest,
  fetchImplForTest,
}: CanvasViewProps): JSX.Element {
  const useStore = storeForTest ?? useAuditStore;
  // useShallow stabilizes the filtered events array · without it the .filter()
  // returns a fresh ref every render and zustand triggers infinite re-renders
  // (especially under React.StrictMode double-render in dev).
  const sessionEvents = useStore(useShallow(selectSessionEvents(sessionId)));

  const nodes = useMemo(() => eventsToNodes(sessionEvents, workspaceId), [sessionEvents, workspaceId]);
  const edges = useMemo(() => eventsToEdges(sessionEvents, workspaceId), [sessionEvents, workspaceId]);
  const impactMarks = useMemo(
    () => (workspaceId ? eventsToImpactMarks(sessionEvents, workspaceId) : []),
    [sessionEvents, workspaceId],
  );
  const activeImpactCount = useMemo(
    () => impactMarks.filter(isActiveImpactMark).length,
    [impactMarks],
  );
  const impactByNode = useMemo(() => impactSummaryByNode(impactMarks), [impactMarks]);
  // codex P2.8.D1 R1 P2 fix · fail-closed when workspaceId is missing.
  const artifacts = useMemo(
    () => (workspaceId ? eventsToArtifacts(sessionEvents, workspaceId) : []),
    [sessionEvents, workspaceId],
  );

  // v0.1 user-feedback 2026-05-12 round 3 · separate connected component
  // nodes (real workflow trees) from isolated legacy nodes (pre-refactor
  // chat-only runs with no edges). Connected nodes go to ReactFlow main
  // viewport · isolated drop to a small "未关联节点" footer · so the new
  // workflow tree doesn't get compressed by legacy noise.
  const { connectedNodes, isolatedNodes } = useMemo(() => {
    const inEdge = new Set<string>();
    for (const e of edges) {
      inEdge.add(e.from_node_id);
      inEdge.add(e.to_node_id);
    }
    return {
      connectedNodes: nodes.filter((n) => inEdge.has(n.node_id)),
      isolatedNodes: nodes.filter((n) => !inEdge.has(n.node_id)),
    };
  }, [nodes, edges]);

  const mainCanvasNodes = connectedNodes.length > 0 ? connectedNodes : nodes;
  const mainCanvasEdges = connectedNodes.length > 0 ? edges : [];
  const largeGraphMode = mainCanvasNodes.length >= CANVAS_LARGE_GRAPH_THRESHOLD;
  const largeGraphSummary = useMemo(
    () => largeGraphMode ? summarizeLargeGraph(mainCanvasNodes, mainCanvasEdges) : null,
    [largeGraphMode, mainCanvasNodes, mainCanvasEdges],
  );

  // v0.1 user-feedback 2026-05-12 round 2 · let dagre do layout · let React
  // Flow render edges with professional routing. Backend's position_x/y is
  // ignored from here on · dagre is the source of truth for layout.
  const rfData = useMemo(
    () => largeGraphMode
      ? { nodes: [], edges: [] }
      : dagreLayout(mainCanvasNodes, mainCanvasEdges, selectedNodeId ?? null, onSelectNode, impactByNode),
    [largeGraphMode, mainCanvasNodes, mainCanvasEdges, selectedNodeId, onSelectNode, impactByNode],
  );

  // Strict copy of design/canvas-first-v5.html § canvas:
  //   .canvas dot-pattern grid · .canvas-toolbar 顶部 pill · .node sticky notes
  //   底部 .legend (color → state)
  return (
    <section className="canvas-panel panel" data-testid="canvas-view">
      <div className="panel-head">
        <div className="panel-title-stack">
          <span className="panel-kicker">工作流</span>
          <span className="title">任务看板</span>
        </div>
        <span className="grow" />
        <span className="panel-count">
          {nodes.length} 节点 · {artifacts.length} 产出 · {activeImpactCount} 影响
        </span>
      </div>
      <div className="canvas" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
        <div className="canvas-toolbar">
          <span className="pill live">实时</span>
          <span className="pill">session · {sessionId}</span>
          <span className="pill" style={{ marginLeft: 'auto', color: 'var(--fg-faint)' }}>
            {edges.length} 连线 {isolatedNodes.length > 0 ? `· ${isolatedNodes.length} 未关联` : ''}
          </span>
        </div>

        {/*
          v0.1 user-feedback 2026-05-12 round 2 · ReactFlow + dagre layout.
          Replaces hand-rolled SVG render. Dagre computes Sugiyama tree
          layout (rankdir=LR), ReactFlow handles edge routing professionally
          (anchored to node Handles, smooth bezier with proper avoidance).
        */}
        <div
          data-testid="canvas-stage"
          style={{
            position: 'relative',
            // v0.1 round 7 · fixed 560 was too short for sessions with
            // many trees · fitView packed them tiny. Use available height
            // (panel-body fill) with a generous floor so a fresh session
            // doesn't collapse to nothing.
            flex: '1 1 auto',
            minHeight: 640,
            padding: 0,
          }}
        >
          {connectedNodes.length === 0 && isolatedNodes.length === 0 ? (
            <div className="canvas-empty" data-testid="canvas-empty-state">
              <strong>还没有任务卡片</strong>
              <span>群聊目标会沉淀为可执行流程</span>
            </div>
          ) : largeGraphMode && largeGraphSummary ? (
            <LargeGraphOverview
              summary={largeGraphSummary}
              selectedNodeId={selectedNodeId}
              onSelectNode={onSelectNode}
              impactByNode={impactByNode}
            />
          ) : (
            <ReactFlow
              data-testid="canvas-nodes"
              nodes={rfData.nodes}
              edges={rfData.edges}
              nodeTypes={RF_NODE_TYPES}
              fitView
              fitViewOptions={{ padding: 0.15, maxZoom: 1.0 }}
              minZoom={0.2}
              maxZoom={1.6}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag
              zoomOnScroll
              onNodeClick={(_evt, n) => onSelectNode?.(n.id)}
              proOptions={{ hideAttribution: true }}
              style={{ background: 'transparent' }}
            >
              <Background gap={16} size={1} color="var(--fg-faint, #cbd5e1)" />
              <Controls
                showInteractive={false}
                style={{ background: 'var(--bg-base, #fff)', border: '1px solid var(--fg-faint, #cbd5e1)' }}
              />
              {/* Edge legend overlay · helps users decode the 3 edge kinds */}
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 5,
                  background: 'var(--bg-base, #fff)',
                  border: '1px solid var(--fg-faint, #cbd5e1)',
                  borderRadius: 4,
                  padding: '6px 10px',
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: 'var(--fg-soft, #475569)',
                  pointerEvents: 'none',
                  fontFamily: 'var(--font-body)',
                }}
              >
                <div style={{ marginBottom: 2, fontWeight: 600 }}>边的含义</div>
                <div>
                  <span style={{ display: 'inline-block', width: 18, borderTop: '1.5px solid var(--fg-faint, #94a3b8)', verticalAlign: 'middle', marginRight: 4 }} />
                  flow · 拆解
                </div>
                <div>
                  <span style={{ display: 'inline-block', width: 18, borderTop: '1.5px dashed var(--accent-warning, #f59e0b)', verticalAlign: 'middle', marginRight: 4 }} />
                  dependency · 依赖
                </div>
                <div>
                  <span style={{ display: 'inline-block', width: 18, borderTop: '1.5px solid var(--accent-primary, #3b82f6)', verticalAlign: 'middle', marginRight: 4 }} />
                  data · 复用上游产出
                </div>
              </div>
            </ReactFlow>
          )}
        </div>

        <ImpactPreviewPanel
          marks={impactMarks}
          nodes={nodes}
          events={sessionEvents}
          workspaceId={workspaceId}
          sessionId={sessionId}
          backendUrl={backendUrl}
          authToken={authToken}
          fetchImpl={fetchImplForTest}
          onSelectNode={onSelectNode}
        />

        {/* Isolated legacy nodes · collapsed footer */}
        {connectedNodes.length > 0 && isolatedNodes.length > 0 && (
          <details
            data-testid="canvas-isolated"
            style={{
              padding: '0 24px 56px',
              fontSize: 12,
              color: 'var(--fg-faint)',
            }}
          >
            <summary style={{ cursor: 'pointer', padding: '6px 0' }}>
              未关联的历史节点（{isolatedNodes.length}）· 点击展开
            </summary>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                paddingTop: 8,
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {isolatedNodes.map((n) => (
                <button
                  type="button"
                  key={n.node_id}
                  data-testid={`isolated-node-${n.node_id}`}
                  data-status={n.status}
                  data-selected={selectedNodeId === n.node_id ? 'true' : undefined}
                  data-superseded={n.superseded_at ? 'true' : undefined}
                  aria-label={`查看节点 ${n.title ?? n.node_id}`}
                  onClick={() => onSelectNode?.(n.node_id)}
                  style={{
                    fontSize: 11,
                    padding: '4px 8px',
                    border: selectedNodeId === n.node_id
                      ? '1px solid var(--accent-primary)'
                      : '1px dashed var(--fg-faint)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    background: 'var(--bg-muted, #f1f5f9)',
                    opacity: n.superseded_at ? 0.48 : 1,
                    color: 'inherit',
                    fontFamily: 'var(--font-body)',
                    textAlign: 'left',
                  }}
                >
                  {n.title?.slice(0, 24) ?? `${n.node_id.slice(0, 6)}…`}
                  <span style={{ marginLeft: 6, opacity: 0.6 }}>{STATUS_LABEL[n.status]}</span>
                </button>
              ))}
            </div>
          </details>
        )}

        <SessionArtifactPanel
          artifacts={artifacts}
          events={sessionEvents}
          nodes={nodes}
          workspaceId={workspaceId}
          backendUrl={backendUrl}
          authToken={authToken}
          fetchImpl={fetchImplForTest}
          onSelectNode={onSelectNode}
        />

        <div className="legend" aria-label="节点状态色板">
          <div className="row">
            <span className="sw" style={{ background: 'var(--bg-muted)' }} /> 休眠
          </div>
          <div className="row">
            <span className="sw" style={{ background: 'var(--accent-warning)' }} /> 运行
          </div>
          <div className="row">
            <span className="sw" style={{ background: 'var(--accent-primary)' }} /> 思考
          </div>
          <div className="row">
            <span className="sw" style={{ background: 'var(--accent-success)' }} /> 完成
          </div>
          <div className="row">
            <span className="sw" style={{ background: 'var(--accent-danger)' }} /> 阻塞
          </div>
          <div className="row">
            <span className="sw" style={{ background: '#fff6a8' }} /> 知识
          </div>
        </div>
      </div>
    </section>
  );
}

interface ImpactPreviewGroup {
  key: string;
  source_kind: CanvasImpactSourceKind;
  source_id: string;
  source_node_id: string | null;
  source_run_id: string | null;
  marks: CanvasImpactMark[];
  directCount: number;
  indirectCount: number;
  dirtyCount: number;
  staleCount: number;
}

const IMPACT_SOURCE_LABEL: Record<CanvasImpactSourceKind, string> = {
  node: '节点',
  node_output: '产出',
  artifact: '文件',
  knowledge: '知识',
};

const IMPACT_STATUS_LABEL: Record<CanvasImpactStatus, string> = {
  dirty: '需重跑',
  stale: '需复核',
  acknowledged: '已保留',
  rerun_queued: '已排队',
  resolved: '已解决',
};

function groupImpactMarks(marks: CanvasImpactMark[]): ImpactPreviewGroup[] {
  const groups = new Map<string, ImpactPreviewGroup>();
  for (const mark of marks) {
    if (!isActiveImpactMark(mark)) continue;
    const key = `${mark.source_kind}:${mark.source_id}`;
    const group = groups.get(key) ?? {
      key,
      source_kind: mark.source_kind,
      source_id: mark.source_id,
      source_node_id: mark.source_node_id,
      source_run_id: mark.source_run_id,
      marks: [],
      directCount: 0,
      indirectCount: 0,
      dirtyCount: 0,
      staleCount: 0,
    };
    group.marks.push(mark);
    if (mark.impact_kind === 'direct') group.directCount += 1;
    else group.indirectCount += 1;
    if (mark.impact_status === 'dirty') group.dirtyCount += 1;
    if (mark.impact_status === 'stale') group.staleCount += 1;
    groups.set(key, group);
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      marks: [...group.marks].sort((a, b) => a.depth - b.depth || a.affected_node_id.localeCompare(b.affected_node_id)),
    }))
    .sort((a, b) => {
      const newestA = Math.max(...a.marks.map((mark) => mark.updated_at ?? mark.last_audit_id));
      const newestB = Math.max(...b.marks.map((mark) => mark.updated_at ?? mark.last_audit_id));
      return newestB - newestA || a.key.localeCompare(b.key);
    });
}

function impactSourceTitle(group: ImpactPreviewGroup, nodesById: Map<string, CanvasNode>): string {
  if (group.source_node_id) {
    const node = nodesById.get(group.source_node_id);
    if (node?.title) return `${IMPACT_SOURCE_LABEL[group.source_kind]} · ${node.title}`;
  }
  return `${IMPACT_SOURCE_LABEL[group.source_kind]} · ${group.source_id.slice(0, 42)}`;
}

function impactCauseText(mark: CanvasImpactMark, nodesById: Map<string, CanvasNode>): string {
  const lastStep = mark.cause_chain[mark.cause_chain.length - 1] ?? {};
  const kind = textField(lastStep.kind);
  if (kind === 'context_ref') {
    const reason = textField(lastStep.reason);
    const refKind = textField(lastStep.ref_kind) ?? 'context';
    const refId = textField(lastStep.ref_id);
    return reason
      ? `${refKind} 引用 · ${reason}`
      : `${refKind} 引用${refId ? ` · ${refId}` : ''}`;
  }
  if (kind === 'edge') {
    const from = textField(lastStep.from_node_id);
    const fromNode = from ? nodesById.get(from) : null;
    const edgeKind = textField(lastStep.edge_kind) ?? 'edge';
    return `${edgeKind} 连线 · 来自 ${fromNode?.title ?? from ?? '上游节点'}`;
  }
  return mark.impact_kind === 'direct' ? '直接引用上游变更' : `间接受影响 · 深度 ${mark.depth}`;
}

function impactEstimateText(node: CanvasNode | undefined, run: CanvasNodeRun | undefined): string {
  const duration = run?.ended_ts && run.created_ts && run.ended_ts >= run.created_ts
    ? `${Math.max(1, Math.round((run.ended_ts - run.created_ts) / 1000))}s`
    : null;
  if (run?.cost_tokens_in || run?.cost_tokens_out || run?.cost_usd_micros) {
    const tokens = (run.cost_tokens_in ?? 0) + (run.cost_tokens_out ?? 0);
    const cost = run.cost_usd_micros ? `$${(run.cost_usd_micros / 1_000_000).toFixed(4)}` : null;
    return `${tokens > 0 ? `${tokens} tokens` : '有成本记录'}${cost ? ` · ${cost}` : ''}${duration ? ` · ${duration}` : ''}`;
  }
  if (duration) return `最近耗时 ${duration}`;
  if (node?.assigned_agent_id) return `按 @${node.assigned_agent_id} 队列重跑`;
  return '暂无成本记录';
}

function selectedImpactNodeIds(
  selectedBySource: Record<string, string[]>,
  group: ImpactPreviewGroup,
): string[] {
  const fallback = group.marks.map((mark) => mark.affected_node_id);
  const selected = selectedBySource[group.key];
  if (!selected) return fallback;
  const allowed = new Set(fallback);
  return selected.filter((nodeId) => allowed.has(nodeId));
}

function ImpactPreviewPanel({
  marks,
  nodes,
  events,
  workspaceId,
  sessionId,
  backendUrl,
  authToken,
  fetchImpl,
  onSelectNode,
}: {
  marks: CanvasImpactMark[];
  nodes: CanvasNode[];
  events: WsEvent[];
  workspaceId?: string;
  sessionId: string;
  backendUrl?: string;
  authToken?: string;
  fetchImpl?: typeof fetch;
  onSelectNode?: (nodeId: string) => void;
}): JSX.Element {
  const [selectedBySource, setSelectedBySource] = useState<Record<string, string[]>>({});
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const groups = useMemo(() => groupImpactMarks(marks), [marks]);
  const nodesById = useMemo(() => new Map(nodes.map((node) => [node.node_id, node])), [nodes]);
  const latestRunsByNode = useMemo(() => {
    const impactedNodeIds = new Set(marks.map((mark) => mark.affected_node_id));
    const out = new Map<string, CanvasNodeRun>();
    for (const nodeId of impactedNodeIds) {
      const runs = eventsToRunsForNode(events, nodeId, workspaceId);
      const latest = runs[runs.length - 1];
      if (latest) out.set(nodeId, latest);
    }
    return out;
  }, [events, marks, workspaceId]);
  const canAct = Boolean(backendUrl && authToken);

  if (groups.length === 0) {
    return <section data-testid="impact-preview-panel" style={{ display: 'none' }} aria-hidden="true" />;
  }

  async function runAction(group: ImpactPreviewGroup, action: CanvasImpactAction, affectedNodeIds?: string[]): Promise<void> {
    if (!backendUrl || !authToken) {
      setMessage('需要登录后才能处理影响面');
      return;
    }
    const key = `${group.key}:${action}`;
    setPendingKey(key);
    setMessage(null);
    try {
      const result = await applyImpactAction({
        backendUrl,
        authToken,
        sessionId,
        sourceKind: group.source_kind,
        sourceId: group.source_id,
        action,
        affectedNodeIds,
        budgetNodeLimit: 32,
        maxDepth: 16,
        fetchImpl,
      });
      if (!result.ok) {
        setMessage(result.error ?? '影响处理失败');
        return;
      }
      const count = result.rerun_node_ids?.length ?? affectedNodeIds?.length ?? group.marks.length;
      setMessage(action === 'rerun_all' || action === 'rerun_selected'
        ? `已提交 ${count} 个节点的重跑请求`
        : '已记录处理选择');
    } catch (e: any) {
      setMessage(String(e?.message ?? e));
    } finally {
      setPendingKey(null);
    }
  }

  function toggleNode(group: ImpactPreviewGroup, nodeId: string): void {
    setSelectedBySource((cur) => {
      const current = selectedImpactNodeIds(cur, group);
      const next = current.includes(nodeId)
        ? current.filter((id) => id !== nodeId)
        : [...current, nodeId];
      return { ...cur, [group.key]: next };
    });
  }

  return (
    <section data-testid="impact-preview-panel" aria-label="影响预览" style={impactPanelStyle}>
      <div style={impactPanelHeadStyle}>
        <div style={impactPanelTitleStyle}>
          <Icon name="alert" size={15} />
          <span>影响预览</span>
          <span style={impactCountStyle}>{groups.reduce((sum, group) => sum + group.marks.length, 0)}</span>
        </div>
        {message ? <span data-testid="impact-action-message" style={impactMessageStyle}>{message}</span> : null}
      </div>
      <div style={impactGroupListStyle}>
        {groups.map((group) => {
          const selectedIds = selectedImpactNodeIds(selectedBySource, group);
          const allSelected = selectedIds.length === group.marks.length;
          const pendingPrefix = `${group.key}:`;
          return (
            <article key={group.key} data-testid={`impact-source-${group.source_kind}-${group.source_id}`} style={impactGroupStyle}>
              <div style={impactGroupHeadStyle}>
                <button
                  type="button"
                  style={impactSourceButtonStyle}
                  onClick={() => group.source_node_id ? onSelectNode?.(group.source_node_id) : undefined}
                  disabled={!group.source_node_id || !onSelectNode}
                  title={group.source_node_id ? '定位来源节点' : '来源节点未记录'}
                >
                  <Icon name="target" size={13} />
                  <span>{impactSourceTitle(group, nodesById)}</span>
                </button>
                <span style={impactGroupMetaStyle}>
                  {group.directCount} 直接 · {group.indirectCount} 间接 · {group.dirtyCount} 需重跑 · {group.staleCount} 需复核
                </span>
              </div>
              <div style={impactRowsStyle}>
                {group.marks.map((mark) => {
                  const node = nodesById.get(mark.affected_node_id);
                  const selected = selectedIds.includes(mark.affected_node_id);
                  return (
                    <div
                      key={`${group.key}:${mark.affected_node_id}`}
                      data-testid={`impact-row-${mark.affected_node_id}`}
                      data-impact-kind={mark.impact_kind}
                      data-impact-status={mark.impact_status}
                      style={impactRowStyle(selected)}
                    >
                      <input
                        type="checkbox"
                        aria-label={`选择 ${node?.title ?? mark.affected_node_id}`}
                        checked={selected}
                        onChange={() => toggleNode(group, mark.affected_node_id)}
                      />
                      <button
                        type="button"
                        onClick={() => onSelectNode?.(mark.affected_node_id)}
                        disabled={!onSelectNode}
                        style={impactNodeButtonStyle(Boolean(onSelectNode))}
                      >
                        <span style={impactNodeTitleStyle}>{node?.title ?? mark.affected_node_id}</span>
                        <span style={impactNodeMetaStyle}>
                          {mark.impact_kind === 'direct' ? '直接' : `间接 L${mark.depth}`} · {IMPACT_STATUS_LABEL[mark.impact_status]}
                        </span>
                      </button>
                      <div style={impactReasonStyle}>{impactCauseText(mark, nodesById)}</div>
                      <div style={impactEstimateStyle}>{impactEstimateText(node, latestRunsByNode.get(mark.affected_node_id))}</div>
                    </div>
                  );
                })}
              </div>
              <div style={impactActionBarStyle}>
                <button
                  type="button"
                  disabled={!canAct || pendingKey?.startsWith(pendingPrefix)}
                  onClick={() => { void runAction(group, 'rerun_all'); }}
                  style={impactActionButtonStyle(canAct && !pendingKey?.startsWith(pendingPrefix), true)}
                >
                  <Icon name={pendingKey === `${group.key}:rerun_all` ? 'spinner' : 'refresh'} size={13} />
                  全部重跑
                </button>
                <button
                  type="button"
                  disabled={!canAct || selectedIds.length === 0 || pendingKey?.startsWith(pendingPrefix)}
                  onClick={() => { void runAction(group, 'rerun_selected', selectedIds); }}
                  style={impactActionButtonStyle(canAct && selectedIds.length > 0 && !pendingKey?.startsWith(pendingPrefix), false)}
                >
                  <Icon name="check" size={13} />
                  仅选中
                </button>
                <button
                  type="button"
                  disabled={!canAct || selectedIds.length === 0 || pendingKey?.startsWith(pendingPrefix)}
                  onClick={() => { void runAction(group, 'keep_old', selectedIds); }}
                  style={impactActionButtonStyle(canAct && selectedIds.length > 0 && !pendingKey?.startsWith(pendingPrefix), false)}
                >
                  <Icon name="eye" size={13} />
                  保留旧值
                </button>
                <button
                  type="button"
                  disabled={!canAct || selectedIds.length === 0 || pendingKey?.startsWith(pendingPrefix)}
                  onClick={() => { void runAction(group, 'branch', selectedIds); }}
                  style={impactActionButtonStyle(canAct && selectedIds.length > 0 && !pendingKey?.startsWith(pendingPrefix), false)}
                >
                  <Icon name="branch" size={13} />
                  开分支
                </button>
                <button
                  type="button"
                  style={impactSelectAllButtonStyle}
                  onClick={() => {
                    setSelectedBySource((cur) => ({
                      ...cur,
                      [group.key]: allSelected ? [] : group.marks.map((mark) => mark.affected_node_id),
                    }));
                  }}
                >
                  {allSelected ? '清空选择' : '全选'}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

interface SessionArtifactPanelProps {
  artifacts: CanvasArtifact[];
  events: WsEvent[];
  nodes: CanvasNode[];
  workspaceId?: string;
  backendUrl?: string;
  authToken?: string;
  fetchImpl?: typeof fetch;
  onSelectNode?: (nodeId: string) => void;
}

function SessionArtifactPanel({
  artifacts,
  events,
  nodes,
  workspaceId,
  backendUrl,
  authToken,
  fetchImpl,
  onSelectNode,
}: SessionArtifactPanelProps): JSX.Element {
  const [filter, setFilter] = useState<ArtifactTypeFilter>('all');
  const [timeFilter, setTimeFilter] = useState<ArtifactTimeFilter>('all');
  const [openedArtifactKey, setOpenedArtifactKey] = useState<string | null>(null);
  const nodesById = useMemo(() => new Map(nodes.map((node) => [node.node_id, node])), [nodes]);
  const visibleArtifacts = useMemo(
    () => artifactsForPanel(artifacts, filter, timeFilter),
    [artifacts, filter, timeFilter],
  );
  const lineagesByKey = useMemo(() => {
    const out = new Map<string, CanvasArtifactLineage>();
    for (const artifact of visibleArtifacts) {
      out.set(artifactPanelKey(artifact), buildArtifactLineage(artifact, events, nodes, workspaceId));
    }
    return out;
  }, [events, nodes, visibleArtifacts, workspaceId]);
  const visibleKeys = useMemo(() => new Set(visibleArtifacts.map((artifact) => artifactPanelKey(artifact))), [visibleArtifacts]);
  const expandedKey = openedArtifactKey && visibleKeys.has(openedArtifactKey)
    ? openedArtifactKey
    : (visibleArtifacts.length > 0 ? artifactPanelKey(visibleArtifacts[0]) : null);

  if (artifacts.length === 0) {
    return <section data-testid="canvas-artifacts" style={{ display: 'none' }} aria-hidden="true" />;
  }

  return (
    <section data-testid="canvas-artifacts" aria-label="产出" style={artifactPanelStyle}>
      <div style={artifactPanelHeadStyle}>
        <div style={artifactPanelTitleStyle}>
          <Icon name="file" size={15} />
          <span>产出</span>
          <span style={artifactCountStyle}>{artifacts.length}</span>
        </div>
        <div style={artifactFilterGroupStyle} aria-label="产出类型过滤">
          {(['all', 'pdf', 'text', 'image', 'other'] as ArtifactTypeFilter[]).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setFilter(item)}
              data-testid={`canvas-artifact-filter-${item}`}
              aria-pressed={filter === item}
              style={artifactFilterButtonStyle(filter === item)}
            >
              {ARTIFACT_FILTER_LABEL[item]}
            </button>
          ))}
        </div>
        <div style={artifactFilterGroupStyle} aria-label="产出时间过滤">
          {(['all', 'recent'] as ArtifactTimeFilter[]).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setTimeFilter(item)}
              data-testid={`canvas-artifact-time-${item}`}
              aria-pressed={timeFilter === item}
              style={artifactFilterButtonStyle(timeFilter === item)}
            >
              {ARTIFACT_TIME_FILTER_LABEL[item]}
            </button>
          ))}
        </div>
      </div>
      {visibleArtifacts.length === 0 ? (
        <div data-testid="canvas-artifacts-empty-filter" style={artifactEmptyStyle}>
          当前过滤条件无产出
        </div>
      ) : (
        <div style={artifactListStyle}>
          {visibleArtifacts.map((artifact) => (
            <SessionArtifactRow
              key={`${artifact.path}:${artifact.audit_id}`}
              artifact={artifact}
              lineage={lineagesByKey.get(artifactPanelKey(artifact)) ?? buildArtifactLineage(artifact, events, nodes, workspaceId)}
              nodesById={nodesById}
              expanded={artifactPanelKey(artifact) === expandedKey}
              onToggleDetail={() => setOpenedArtifactKey(artifactPanelKey(artifact))}
              backendUrl={backendUrl}
              authToken={authToken}
              fetchImpl={fetchImpl}
              onSelectNode={onSelectNode}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface SessionArtifactRowProps {
  artifact: CanvasArtifact;
  lineage: CanvasArtifactLineage;
  nodesById: Map<string, CanvasNode>;
  expanded: boolean;
  onToggleDetail: () => void;
  backendUrl?: string;
  authToken?: string;
  fetchImpl?: typeof fetch;
  onSelectNode?: (nodeId: string) => void;
}

function SessionArtifactRow({
  artifact,
  lineage,
  nodesById,
  expanded,
  onToggleDetail,
  backendUrl,
  authToken,
  fetchImpl,
  onSelectNode,
}: SessionArtifactRowProps): JSX.Element {
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflightRef = useRef(false);
  const filename = artifactFilename(artifact);
  const canDownload = Boolean(backendUrl && authToken);
  const primaryProducer = lineage.producedBy.find((entry) => entry.node_id) ?? null;
  const sourceNodeId = artifact.node_id ?? primaryProducer?.node_id ?? null;
  const sourceNode = sourceNodeId ? nodesById.get(sourceNodeId) : undefined;
  const currentVersion = lineage.versions.find((version) => version.current)
    ?? lineage.versions.find((version) => version.hash === artifact.hash)
    ?? lineage.versions[0];
  const versionText = currentVersion
    ? `v${currentVersion.version}/${lineage.versions.length}`
    : 'v1/1';
  const sourceText = sourceNode?.title
    ? `节点 · ${sourceNode.title}`
    : sourceNodeId
      ? `节点 · ${sourceNodeId.slice(0, 8)}`
      : artifact.run_id
        ? `run · ${artifact.run_id.slice(0, 8)}`
        : '来源未记录';

  async function presignCurrentArtifact(): Promise<PresignArtifactDownloadResult | null> {
    if (inflightRef.current) return null;
    if (!backendUrl || !authToken) {
      setError('not configured · login required');
      return null;
    }
    inflightRef.current = true;
    setError(null);
    try {
      const result = await presignArtifactDownload({
        backendUrl,
        authToken,
        hash: artifact.hash,
        filename,
        fetchImpl,
      });
      if (!result.ok || !result.url) {
        setError(result.error ?? 'unknown error');
        return null;
      }
      return result;
    } catch (e: any) {
      setError(String(e?.message ?? e));
      return null;
    } finally {
      inflightRef.current = false;
    }
  }

  async function handlePreview(): Promise<void> {
    setPreviewing(true);
    try {
      const result = await presignCurrentArtifact();
      if (!result?.url) return;
      if (typeof window === 'undefined') {
        setError('preview not supported in this environment');
        return;
      }
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } finally {
      setPreviewing(false);
    }
  }

  async function handleDownload(): Promise<void> {
    setDownloading(true);
    try {
      const result = await presignCurrentArtifact();
      if (!result?.url) return;
      if (typeof document === 'undefined') {
        setError('download not supported in this environment');
        return;
      }
      try {
        const a = document.createElement('a');
        a.href = result.url;
        a.download = filename;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (e: any) {
        setError(String(e?.message ?? e));
      }
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <article data-testid={`canvas-artifact-${artifact.audit_id}`} style={artifactRowStyle}>
      <div style={artifactFileGlyphStyle} aria-hidden="true">
        <Icon name="file" size={14} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={artifactFilenameStyle}>{filename}</div>
        <div style={artifactPathStyle}>{artifact.path}</div>
        <div style={artifactMetaStyle}>
          <span>{formatSize(artifact.size)}</span>
          <span>{artifactTypeLabel(artifact)}</span>
          <span>{versionText}</span>
          <span>{lineage.referencedBy.length} 引用</span>
          <span>{sourceText}</span>
        </div>
      </div>
      <div style={artifactActionsStyle}>
        <button
          type="button"
          onClick={onToggleDetail}
          data-testid={`canvas-artifact-detail-toggle-${artifact.audit_id}`}
          aria-label={`查看 ${filename} 的产物血缘`}
          title="血缘"
          style={artifactIconButtonStyle(true)}
        >
          <Icon name={expanded ? 'chev-down' : 'chev-right'} size={14} />
        </button>
        {sourceNodeId ? (
          <button
            type="button"
            onClick={() => onSelectNode?.(sourceNodeId)}
            disabled={!onSelectNode}
            data-testid={`canvas-artifact-source-${artifact.audit_id}`}
            aria-label={`定位 ${filename} 的来源节点`}
            title="定位来源节点"
            style={artifactIconButtonStyle(Boolean(onSelectNode))}
          >
            <Icon name="target" size={14} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={handlePreview}
          disabled={!canDownload || previewing}
          data-testid={`canvas-artifact-preview-${artifact.audit_id}`}
          aria-label={`预览 ${filename}`}
          title="预览"
          style={artifactIconButtonStyle(canDownload && !previewing)}
        >
          <Icon name={previewing ? 'spinner' : 'eye'} size={14} />
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!canDownload || downloading}
          data-testid={`canvas-artifact-download-${artifact.audit_id}`}
          aria-label={`下载 ${filename}`}
          title="下载"
          style={artifactIconButtonStyle(canDownload && !downloading)}
        >
          <Icon name={downloading ? 'spinner' : 'download'} size={14} />
        </button>
      </div>
      {expanded ? (
        <ArtifactLineageDetail
          artifact={artifact}
          lineage={lineage}
          nodesById={nodesById}
          onSelectNode={onSelectNode}
        />
      ) : null}
      {error ? <div style={artifactErrorStyle}>{error}</div> : null}
    </article>
  );
}

function artifactPanelKey(artifact: CanvasArtifact): string {
  return `${artifact.path}:${artifact.audit_id}:${artifact.hash}`;
}

function ArtifactLineageDetail({
  artifact,
  lineage,
  nodesById,
  onSelectNode,
}: {
  artifact: CanvasArtifact;
  lineage: CanvasArtifactLineage;
  nodesById: Map<string, CanvasNode>;
  onSelectNode?: (nodeId: string) => void;
}): JSX.Element {
  return (
    <div data-testid={`canvas-artifact-detail-${artifact.audit_id}`} style={artifactDetailStyle}>
      <div style={artifactDetailColumnStyle}>
        <div style={artifactDetailTitleStyle}>
          <Icon name="sort" size={12} />
          <span>版本</span>
        </div>
        <div data-testid={`canvas-artifact-versions-${artifact.audit_id}`} style={artifactDetailListStyle}>
          {lineage.versions.map((version) => (
            <div key={`${version.audit_id}:${version.hash}`} style={artifactDetailItemStyle}>
              <span style={artifactDetailStrongStyle}>v{version.version}</span>
              <span>{formatSize(version.size)}</span>
              <span>{version.hash.slice(0, 8)}</span>
              {version.current ? <span style={artifactDetailBadgeStyle}>当前</span> : null}
            </div>
          ))}
        </div>
      </div>

      <div style={artifactDetailColumnStyle}>
        <div style={artifactDetailTitleStyle}>
          <Icon name="target" size={12} />
          <span>由谁产出</span>
        </div>
        <div data-testid={`canvas-artifact-produced-by-${artifact.audit_id}`} style={artifactDetailListStyle}>
          {lineage.producedBy.length === 0 ? (
            <div style={artifactDetailMutedStyle}>来源未记录</div>
          ) : lineage.producedBy.map((entry, index) => {
            const node = entry.node_id ? nodesById.get(entry.node_id) : undefined;
            return (
              <button
                key={`${entry.output_id ?? 'out'}:${entry.run_id ?? 'run'}:${entry.audit_id ?? index}`}
                type="button"
                onClick={() => entry.node_id ? onSelectNode?.(entry.node_id) : undefined}
                disabled={!entry.node_id || !onSelectNode}
                style={artifactDetailButtonStyle(Boolean(entry.node_id && onSelectNode))}
              >
                <span style={artifactDetailStrongStyle}>{node?.title ?? entry.node_id ?? '未知节点'}</span>
                {entry.version ? <span>v{entry.version}</span> : null}
                {entry.output_id ? <span>output · {entry.output_id.slice(0, 8)}</span> : null}
                {entry.run_id ? <span>run · {entry.run_id.slice(0, 8)}</span> : null}
              </button>
            );
          })}
        </div>
      </div>

      <div style={artifactDetailColumnStyle}>
        <div style={artifactDetailTitleStyle}>
          <Icon name="link" size={12} />
          <span>谁引用</span>
        </div>
        <div data-testid={`canvas-artifact-referenced-by-${artifact.audit_id}`} style={artifactDetailListStyle}>
          {lineage.referencedBy.length === 0 ? (
            <div style={artifactDetailMutedStyle}>暂无下游引用</div>
          ) : lineage.referencedBy.map((entry, index) => (
            <button
              key={`${entry.node_id}:${entry.source}:${entry.ref_id ?? index}`}
              type="button"
              onClick={() => onSelectNode?.(entry.node_id)}
              disabled={!onSelectNode}
              style={artifactDetailButtonStyle(Boolean(onSelectNode))}
            >
              <span style={artifactDetailStrongStyle}>{entry.title ?? entry.node_id}</span>
              <span>{entry.source === 'merge_ref' ? 'merge' : 'context'}</span>
              {entry.ref_id ? <span>{entry.ref_id.slice(0, 40)}</span> : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const impactPanelStyle: CSSProperties = {
  margin: '0 24px 12px',
  padding: '10px 12px',
  border: '1px solid var(--accent-warning)',
  borderRadius: 6,
  background: 'var(--bg-emphasis)',
};

const impactPanelHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 8,
  minWidth: 0,
};

const impactPanelTitleStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: 'var(--font-title)',
  fontSize: 13,
  fontWeight: 800,
  color: 'var(--fg-default)',
};

const impactCountStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  fontWeight: 800,
  color: 'var(--accent-danger)',
};

const impactMessageStyle: CSSProperties = {
  marginLeft: 'auto',
  minWidth: 0,
  fontSize: 11,
  color: 'var(--fg-soft)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const impactGroupListStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
  maxHeight: 420,
  overflowY: 'auto',
  paddingRight: 2,
};

const impactGroupStyle: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  background: 'var(--bg-base)',
  padding: '8px 9px',
};

const impactGroupHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 7,
  minWidth: 0,
};

const impactSourceButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  minWidth: 0,
  border: '0',
  padding: 0,
  background: 'transparent',
  color: 'var(--fg-default)',
  fontFamily: 'var(--font-title)',
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
};

const impactGroupMetaStyle: CSSProperties = {
  marginLeft: 'auto',
  fontSize: 10,
  color: 'var(--fg-faint)',
  whiteSpace: 'nowrap',
};

const impactRowsStyle: CSSProperties = {
  display: 'grid',
  gap: 5,
};

function impactRowStyle(selected: boolean): CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: '18px minmax(120px, 1.2fr) minmax(140px, 1fr) minmax(96px, auto)',
    alignItems: 'center',
    gap: 7,
    border: selected ? '1px solid var(--accent-primary)' : '1px solid var(--border-soft)',
    borderRadius: 5,
    padding: '6px 7px',
    background: selected ? 'var(--accent-primary-bg)' : 'var(--bg-surface)',
    minHeight: 46,
  };
}

function impactNodeButtonStyle(enabled: boolean): CSSProperties {
  return {
    border: '0',
    background: 'transparent',
    padding: 0,
    minWidth: 0,
    display: 'grid',
    gap: 2,
    textAlign: 'left',
    cursor: enabled ? 'pointer' : 'default',
    color: 'var(--fg-default)',
    fontFamily: 'var(--font-body)',
  };
}

const impactNodeTitleStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 800,
  fontSize: 11,
};

const impactNodeMetaStyle: CSSProperties = {
  fontSize: 10,
  color: 'var(--fg-faint)',
};

const impactReasonStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 10,
  color: 'var(--fg-soft)',
};

const impactEstimateStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  color: 'var(--fg-faint)',
  textAlign: 'right',
};

const impactActionBarStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  alignItems: 'center',
  marginTop: 8,
};

function impactActionButtonStyle(enabled: boolean, primary: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    minHeight: 28,
    padding: '5px 8px',
    border: primary ? '1px solid var(--accent-primary)' : '1px solid var(--border-default)',
    borderRadius: 4,
    background: primary ? 'var(--accent-primary-bg)' : 'var(--bg-base)',
    color: enabled ? 'var(--fg-default)' : 'var(--fg-faint)',
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    fontWeight: primary ? 800 : 700,
  };
}

const impactSelectAllButtonStyle: CSSProperties = {
  marginLeft: 'auto',
  border: '0',
  background: 'transparent',
  color: 'var(--fg-soft)',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'var(--font-body)',
};

const artifactPanelStyle: CSSProperties = {
  margin: '0 24px 12px',
  padding: '10px 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  background: 'var(--bg-surface)',
};

const artifactPanelHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 8,
};

const artifactPanelTitleStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: 'var(--font-title)',
  fontSize: 13,
  fontWeight: 800,
  color: 'var(--fg-default)',
};

const artifactCountStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--fg-faint)',
};

const artifactFilterGroupStyle: CSSProperties = {
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  flexWrap: 'wrap',
};

function artifactFilterButtonStyle(active: boolean): CSSProperties {
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    fontWeight: active ? 800 : 600,
    padding: '3px 7px',
    border: active ? '1px solid var(--accent-primary)' : '1px solid var(--border-default)',
    borderRadius: 4,
    background: active ? 'var(--bg-emphasis)' : 'var(--bg-base)',
    color: active ? 'var(--fg-default)' : 'var(--fg-soft)',
    cursor: 'pointer',
  };
}

const artifactEmptyStyle: CSSProperties = {
  padding: '8px 4px',
  fontSize: 12,
  color: 'var(--fg-faint)',
};

const artifactListStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: 8,
  maxHeight: 360,
  overflowY: 'auto',
  paddingRight: 2,
};

const artifactRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '24px minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: 8,
  position: 'relative',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  padding: '8px 9px',
  background: 'var(--bg-base)',
  minHeight: 64,
};

const artifactFileGlyphStyle: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--accent-primary)',
  background: 'var(--bg-muted)',
};

const artifactFilenameStyle: CSSProperties = {
  fontFamily: 'var(--font-title)',
  fontSize: 12,
  fontWeight: 800,
  color: 'var(--fg-default)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const artifactPathStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  color: 'var(--fg-soft)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginTop: 1,
};

const artifactMetaStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 4,
  fontSize: 10,
  color: 'var(--fg-faint)',
};

const artifactActionsStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
};

function artifactIconButtonStyle(enabled: boolean): CSSProperties {
  return {
    width: 26,
    height: 26,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--border-default)',
    borderRadius: 4,
    background: enabled ? 'var(--bg-base)' : 'var(--bg-muted)',
    color: enabled ? 'var(--fg-default)' : 'var(--fg-faint)',
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}

const artifactErrorStyle: CSSProperties = {
  gridColumn: '2 / 4',
  fontSize: 10,
  color: 'var(--accent-danger)',
};

const artifactDetailStyle: CSSProperties = {
  gridColumn: '1 / 4',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 8,
  borderTop: '1px solid var(--border-default)',
  paddingTop: 8,
  marginTop: 2,
};

const artifactDetailColumnStyle: CSSProperties = {
  minWidth: 0,
  display: 'grid',
  gap: 5,
};

const artifactDetailTitleStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 10,
  fontWeight: 800,
  color: 'var(--fg-soft)',
};

const artifactDetailListStyle: CSSProperties = {
  display: 'grid',
  gap: 4,
  minWidth: 0,
};

const artifactDetailItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  minWidth: 0,
  fontSize: 10,
  color: 'var(--fg-faint)',
  fontFamily: 'var(--font-mono)',
};

const artifactDetailStrongStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--fg-default)',
  fontWeight: 800,
};

const artifactDetailBadgeStyle: CSSProperties = {
  border: '1px solid var(--accent-success)',
  borderRadius: 4,
  padding: '1px 4px',
  color: 'var(--accent-success)',
  fontSize: 9,
  fontWeight: 800,
};

const artifactDetailMutedStyle: CSSProperties = {
  fontSize: 10,
  color: 'var(--fg-faint)',
};

function artifactDetailButtonStyle(enabled: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 0,
    border: '1px solid var(--border-default)',
    borderRadius: 4,
    padding: '4px 5px',
    background: 'var(--bg-base)',
    color: enabled ? 'var(--fg-soft)' : 'var(--fg-faint)',
    cursor: enabled ? 'pointer' : 'default',
    fontSize: 10,
    textAlign: 'left',
  };
}

const largeGraphSectionStyle: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  background: 'var(--bg-base)',
  padding: 12,
  boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
};

const largeGraphSectionHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  marginBottom: 8,
  fontSize: 13,
  color: 'var(--fg-default)',
};

const largeGraphHintStyle: CSSProperties = {
  margin: '0 0 10px',
  fontSize: 12,
  lineHeight: 1.55,
  color: 'var(--fg-soft)',
};

const largeGraphMetricStyle: CSSProperties = {
  display: 'grid',
  gap: 4,
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  background: 'var(--bg-base)',
  padding: '10px 12px',
  minWidth: 0,
};

const largeGraphEdgeLegendStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginBottom: 10,
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  color: 'var(--fg-faint)',
};

const largeGraphGroupRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(80px, 1fr) repeat(4, auto)',
  gap: 8,
  alignItems: 'center',
  border: '1px solid var(--border-muted)',
  borderRadius: 4,
  padding: '6px 8px',
  fontSize: 11,
  color: 'var(--fg-soft)',
  minWidth: 0,
};

function nodeImpactBadgeStyle(status: CanvasImpactStatus): CSSProperties {
  const isDirty = status === 'dirty';
  const isQueued = status === 'rerun_queued';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 4,
    border: `1px solid ${isDirty ? 'var(--accent-danger)' : isQueued ? 'var(--accent-primary)' : 'var(--accent-warning)'}`,
    background: isDirty
      ? 'var(--accent-danger-bg)'
      : isQueued
        ? 'var(--accent-primary-bg)'
        : 'var(--accent-warning-bg)',
    color: 'var(--fg-default)',
    padding: '1px 5px',
    fontSize: 9,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  };
}

/** Sketch-theme sticky-note node · status-class drives bg/border colors.
 *  P3 P0-4 · selected gets a thicker border + accent ring. */
function NodeCard({
  node,
  selected,
  relation,
  impact,
  onSelect,
}: {
  node: CanvasNode;
  selected?: boolean;
  relation?: CanvasNodeRelation;
  impact?: CanvasImpactNodeSummary;
  onSelect?: (nodeId: string) => void;
}): JSX.Element {
  const isOutput = node.node_kind === 'output';
  const isHuman = node.node_kind === 'human';
  const isKnowledge = isOutput || isHuman;
  const cls = isKnowledge ? `node knowledge` : `node ${node.status}`;
  const isSuperseded = node.superseded_at !== null;
  const graphRelation = relation ?? (selected ? 'selected' : 'unrelated');
  // v0.1 user-feedback 2026-05-12 · output 节点是产出物 · 视觉上明显区分
  // (实心实色边框 + 📦 图标 + 标签)，避免和中间 task 节点混淆。
  const outputStyle: React.CSSProperties | undefined = isOutput ? {
    border: '2px solid var(--accent-success, #16a34a)',
    borderStyle: 'double',
    background: 'var(--bg-emphasis, #fef3c7)',
  } : undefined;
  return (
    <div
      className={cls}
      data-testid={`canvas-node-${node.node_id}`}
      data-status={node.status}
      data-kind={node.node_kind}
      data-selected={selected ? 'true' : undefined}
      data-relation={graphRelation}
      data-superseded={isSuperseded ? 'true' : undefined}
      onClick={(e) => {
        // Stop propagation · AiwTaskNode wrapper also has an onClick that
        // calls onSelect. Without this · the toggle fires twice and the
        // selection cancels out (selected → null in one user click).
        e.stopPropagation();
        onSelect?.(node.node_id);
      }}
      style={{
        cursor: 'pointer',
        ...outputStyle,
        ...(isSuperseded ? {
          opacity: 0.48,
          filter: 'grayscale(0.6)',
          borderStyle: 'dashed',
        } : {}),
        ...(selected ? {
          outline: '2.5px solid var(--accent-primary)',
          outlineOffset: '3px',
        } : graphRelation === 'upstream' ? {
          outline: '2px dashed var(--accent-warning)',
          outlineOffset: '2px',
        } : graphRelation === 'downstream' ? {
          outline: '2px solid var(--accent-primary)',
          outlineOffset: '2px',
        } : relation ? {
          opacity: 0.58,
        } : {}),
      }}
    >
      <div className="node-head">
        <span className="node-kind">{KIND_LABEL[node.node_kind]}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {impact ? (
            <span
              data-testid={`canvas-node-impact-${node.node_id}`}
              title={`${impact.direct_count} 直接 · ${impact.indirect_count} 间接`}
              style={nodeImpactBadgeStyle(impact.highest_status)}
            >
              {IMPACT_STATUS_LABEL[impact.highest_status]} · {impact.active_count}
            </span>
          ) : null}
          <span className="node-status">{STATUS_LABEL[node.status]}</span>
        </span>
      </div>
      <div className="node-title" title={node.title ?? node.node_id}>
        {isOutput ? '产出 · ' : isHuman ? '人工 · ' : ''}
        {node.title ?? `${node.node_id.slice(0, 8)}…`}
      </div>
      {node.description ? (
        <div className="node-desc" title={node.description}>
          {node.description}
        </div>
      ) : null}
      <div className="node-meta">
        <span className="dot-type" />
        <span>{node.source_audit_start ? `audit ${node.source_audit_start}` : node.node_id.slice(0, 8)}</span>
        {node.assigned_agent_id ? (
          <>
            <span>·</span>
            <span className="node-owner">@{node.assigned_agent_id}</span>
          </>
        ) : null}
      </div>
    </div>
  );
}

// Pre-sketch helpers (P2.6.c) replaced by NodeCard above.
// Round 4 (2026-05-12) · ArtifactCard removed · artifacts now render in
// NodeDetailPanel.ArtifactRow when a node is selected. `presignArtifactDownload`
// kept (NodeDetailPanel imports it).
